import React, { useEffect, useState } from 'react';
import {
  Typography,
  Breadcrumb,
  Card,
  Table,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  message,
  Popconfirm,
  Alert,
  Upload,
  Divider,
  Pagination,
  Checkbox,
  Slider,
  Space,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FacebookFilled, CheckCircleFilled } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;

const AllCampaignsList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // Wizard state
  const [step, setStep] = useState(0); // 0: Select Client, 1: Ad Type, (future) 2: Upload Profiles, 3: Upload GPS, 4: Locations, 5: Results
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [adType, setAdType] = useState(null); // 'NeighborhoodLens' | 'Geofence'
  // Step 2: Upload Profiles
  const [audienceFiles, setAudienceFiles] = useState([]); // from /api/media
  const [audienceFilesLoading, setAudienceFilesLoading] = useState(false);
  const [selectedAudienceFileId, setSelectedAudienceFileId] = useState(null);
  const [uploadingProfiles, setUploadingProfiles] = useState(false);
  const [profilesCount, setProfilesCount] = useState(null);
  const [profiles, setProfiles] = useState([]); // parsed audience profiles from CSV
  const [standardizingAddresses, setStandardizingAddresses] = useState(false);
  const [addressesStandardized, setAddressesStandardized] = useState(false);
  // Step 3: Upload GPS
  const [gpsFiles, setGpsFiles] = useState([]);
  const [gpsFilesLoading, setGpsFilesLoading] = useState(false);
  const [selectedGpsFileId, setSelectedGpsFileId] = useState(null);
  const [uploadingGps, setUploadingGps] = useState(false);
  const [gpsLocations, setGpsLocations] = useState([]); // {id, address, durationMin, visitAt}
  const [gpsCount, setGpsCount] = useState(null);
  // Get Address from Geotab
  const [geotabAddresses, setGeotabAddresses] = useState([]);
  const [fetchingAddresses, setFetchingAddresses] = useState(false);
  const [addressesFetched, setAddressesFetched] = useState(false);
  const [selectedGeotabIds, setSelectedGeotabIds] = useState([]);
  const [maxAddresses, setMaxAddresses] = useState(50);
  // Geotab DB selection
  const [geotabDbOptions, setGeotabDbOptions] = useState([]); // [{value,label}]
  const [selectedDatabases, setSelectedDatabases] = useState([]); // values of database names
  const [geotabDateRange, setGeotabDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [stopDurationMin, setStopDurationMin] = useState(0);
  const [stopDurationMax, setStopDurationMax] = useState(null);
  // Address filters
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterZipcode, setFilterZipcode] = useState('');
  const [selectedGpsIds, setSelectedGpsIds] = useState([]);
  // Step 4: Locations (geofence radius)
  const [radiusMiles, setRadiusMiles] = useState(1);
  const [gpsDeletingId, setGpsDeletingId] = useState(null);
  // Step 5: Results
  const [resultsProfiles, setResultsProfiles] = useState([]);
  const [resultsSelectedIds, setResultsSelectedIds] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [resultsPageSize] = useState(50);

  // Facebook Ad Wizard (Geofence launch)
  const [fbWizardOpen, setFbWizardOpen] = useState(false);
  const [fbStep, setFbStep] = useState(1); // 1..7
  const [fbAudienceName, setFbAudienceName] = useState(() => `PathMarkr Audience ${dayjs().format('M/D/YYYY')}`);
  const [fbAudienceDesc, setFbAudienceDesc] = useState('Custom audience created from PathMarkr neighborhood targeting...');
  const [fbCampaignName, setFbCampaignName] = useState('Summer Sale 2024');
  const [fbObjective, setFbObjective] = useState(undefined);
  const [fbAdName, setFbAdName] = useState('Summer Sale Banner Ad');
  const [fbAgeMin, setFbAgeMin] = useState(25);
  const [fbAgeMax, setFbAgeMax] = useState(65);
  const [fbGender, setFbGender] = useState('all');
  const [fbLocation, setFbLocation] = useState([]);
  const [fbInterests, setFbInterests] = useState([]);
  const [fbHeadline, setFbHeadline] = useState('Get 20% Off This Summer!');
  const [fbDescription, setFbDescription] = useState('Limited time offer for new customers');
  const [fbCta, setFbCta] = useState(undefined);
  const [fbWebsiteUrl, setFbWebsiteUrl] = useState('https://yourbusiness.com');
  const [fbImageFile, setFbImageFile] = useState(null);
  const [fbVideoFile, setFbVideoFile] = useState(null);
  const [fbMediaType, setFbMediaType] = useState('image'); // 'image' | 'video'
  // Budget & Schedule
  const [fbBudgetType, setFbBudgetType] = useState('daily'); // 'daily' | 'lifetime'
  const [fbDailyBudget, setFbDailyBudget] = useState(20);
  const [fbDurationDays, setFbDurationDays] = useState(7);
  // Upload Custom Audience (CSV)
  const [fbAudienceCsvFile, setFbAudienceCsvFile] = useState(null);
  const [fbAudienceRowCount, setFbAudienceRowCount] = useState(null);
  const [fbAudienceUploading, setFbAudienceUploading] = useState(false);
  const [fbAudienceFileId, setFbAudienceFileId] = useState(null);
  const API_BASE_URL = '/api/campaigns';
  const USERS_API = '/api/users';
  const MEDIA_API = '/api/media';
  const ADDRESS_SEARCH_API = '/api/address-search';
  const FB_ADS_URL = process.env.REACT_APP_FB_ADS_URL || 'https://www.facebook.com/adsmanager/create/campaign';
  const GA_ADS_URL = process.env.REACT_APP_GA_ADS_URL || 'https://ads.google.com/aw/campaigns/new';

  // Google Ads Wizard (Geofence launch)
  const [gaWizardOpen, setGaWizardOpen] = useState(false);
  const [gaStep, setGaStep] = useState(1);
  const [gaCampaignName, setGaCampaignName] = useState('Geofence Campaign');
  const [gaCampaignType, setGaCampaignType] = useState(undefined);
  const [gaCampaignGoal, setGaCampaignGoal] = useState(undefined);
  const [gaBudget, setGaBudget] = useState(20);
  const [gaBiddingStrategy, setGaBiddingStrategy] = useState(undefined);
  const [gaLocations, setGaLocations] = useState([]);
  const [gaLanguages, setGaLanguages] = useState('English');
  const [gaStartDate, setGaStartDate] = useState(null);
  const [gaEndDate, setGaEndDate] = useState(null);
  const [gaAdGroupName, setGaAdGroupName] = useState('Ad Group 1');
  const [gaKeywords, setGaKeywords] = useState('');
  const [gaAudience, setGaAudience] = useState('');
  const [gaHeadline1, setGaHeadline1] = useState('');
  const [gaHeadline2, setGaHeadline2] = useState('');
  const [gaHeadline3, setGaHeadline3] = useState('');
  const [gaDesc1, setGaDesc1] = useState('');
  const [gaDesc2, setGaDesc2] = useState('');
  const [gaFinalUrl, setGaFinalUrl] = useState('https://yourbusiness.com');
  const [gaDisplayPath, setGaDisplayPath] = useState('');
  const [gaCta, setGaCta] = useState(undefined);
  const [gaAdExtensions, setGaAdExtensions] = useState('');
  const [gaTrackingTemplate, setGaTrackingTemplate] = useState('');
  const [gaConversionTracking, setGaConversionTracking] = useState(false);
  
  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: 'Campaign', dataIndex: 'name', key: 'name' },
    { title: 'Client ID', dataIndex: 'client_id', key: 'client_id', width: 110 },
    { title: 'Zone ID', dataIndex: 'zone_id', key: 'zone_id', width: 100 },
    
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => (
        <Tag color={s === 'active' ? 'green' : s === 'paused' ? 'orange' : s === 'completed' ? 'blue' : s === 'draft' ? 'default' : 'red'}>{s}</Tag>
      )
    },
    { title: 'Start', dataIndex: 'start_date', key: 'start_date', width: 140 },
    { title: 'End', dataIndex: 'end_date', key: 'end_date', width: 140 },
    { title: 'Budget', dataIndex: 'budget', key: 'budget', width: 120, render: (v) => `$${Number(v ?? 0).toLocaleString()}` },
    { 
      title: 'Impressions', 
      dataIndex: 'impressions', 
      key: 'impressions', 
      width: 120, 
      render: (v) => Number(v ?? 0).toLocaleString() 
    },
    { 
      title: 'Miles', 
      dataIndex: 'miles', 
      key: 'miles', 
      width: 100, 
      render: (v) => Number(v ?? 0).toFixed(2) 
    },
    { 
      title: 'Impr/Mile', 
      key: 'impressions_per_mile', 
      width: 110, 
      render: (_, record) => {
        const miles = Number(record.miles ?? 0);
        const impressions = Number(record.impressions ?? 0);
        if (miles === 0) return '-';
        return (impressions / miles).toFixed(0);
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this campaign?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(record.id)}
          >
            <Button size="small" type="link" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Fetch media list (audience files) for the selected user
  const fetchMediaList = async () => {
    setAudienceFilesLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUserId) params.set('user_id', selectedUserId);
      // Optionally filter by type if backend supports it
      // params.set('type', 'audience');
      const { data } = await axios.get(`${MEDIA_API}${params.toString() ? `?${params.toString()}` : ''}`, { headers: authHeader() });
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setAudienceFiles(list);
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load media files');
    } finally {
      setAudienceFilesLoading(false);
    }
  };

  // Fetch available Geotab accounts (databases) to allow selecting which DBs to query
  const fetchGeotabAccounts = async () => {
    try {
      const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: authHeader() });
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      const opts = items
        .map(a => a?.database)
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map(db => ({ value: db, label: db }));
      setGeotabDbOptions(opts);
      setSelectedDatabases(opts.map(o => o.value));
    } catch (e) {
      // If there's an error (e.g., no Geotab credentials), set empty options
      setGeotabDbOptions([]);
      setSelectedDatabases([]);
    }
  };

  // Prepare Results: load profiles from selectedAudienceFileId if needed, then apply current selections
  const prepareResultsProfiles = async () => {
    if (!selectedAudienceFileId) {
      message.error('No audience file selected');
      return;
    }
    setResultsLoading(true);
    try {
      console.log('prepareResultsProfiles - selectedAudienceFileId:', selectedAudienceFileId);
      console.log('prepareResultsProfiles - current profiles length:', profiles?.length || 0);
      
      // If we don't have parsed profiles yet, fetch and parse now
      let base = profiles && profiles.length ? profiles : [];
      if (base.length === 0) {
        console.log('No profiles found, fetching and parsing...');
        try {
          const fetchedProfiles = await fetchAndParseAudienceFile(selectedAudienceFileId);
          base = fetchedProfiles || [];
          console.log('After fetchAndParseAudienceFile, fetched profiles length:', base.length);
        } catch (error) {
          console.error('Error fetching profiles:', error);
          // If geocoding fails, try to get just the parsed profiles without standardization
          try {
            const res = await axios.get(`${MEDIA_API}/${selectedAudienceFileId}`, { responseType: 'blob', headers: authHeader() });
            const blob = res?.data;
            if (blob) {
              const text = await blob.text();
              const parsed = parseProfilesCsvText(text);
              base = parsed || [];
              console.log('Fallback: parsed profiles without geocoding:', base.length);
              message.warning(`Loaded ${base.length} profiles without address standardization`);
            }
          } catch (fallbackError) {
            console.error('Fallback parsing also failed:', fallbackError);
            message.error('Failed to load profiles');
            return;
          }
        }
      }
      console.log('Base profiles to work with:', base.length);
      
      // If user previously selected specific profiles in Step 2 and stored in the form, prefer those. Otherwise use all
      const chosenIds = form.getFieldValue('audience_profile_ids');
      console.log('Form chosen IDs:', chosenIds);
      
      const chosen = (Array.isArray(chosenIds) && chosenIds.length > 0)
        ? base.filter(p => chosenIds.includes(p.id))
        : base;
      
      console.log('Final chosen profiles:', chosen.length);
      setResultsProfiles(chosen);
      setResultsSelectedIds([]); // Start with no profiles selected
      setResultsCurrentPage(1); // Reset to first page
      message.success(`Loaded ${chosen.length} profiles - select profiles to launch campaign`);
    } catch (e) {
      console.error('prepareResultsProfiles error:', e);
      message.error('Failed to load profiles: ' + (e.message || 'Unknown error'));
    } finally {
      setResultsLoading(false);
    }
  };

  const exportProfilesCsv = (items) => {
    const rows = [
      ['Street','City','State','Zip','Original Address','Standardized Address','Latitude','Longitude','Coordinates'],
      ...items.map(p => [
        p.street||'', 
        p.city||'', 
        p.state||'', 
        p.zip||'', 
        p.originalAddress||p.address||'',
        p.standardizedAddress||p.address||'',
        p.latitude||'',
        p.longitude||'',
        p.coordinates||''
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `profiles_standardized_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteGpsFile = async (id) => {
    if (!id) return;
    try {
      setGpsDeletingId(id);
      await axios.delete(`${MEDIA_API}/${id}`, { headers: authHeader() });
      message.success('GPS dataset deleted');
      if (selectedGpsFileId === id) {
        setSelectedGpsFileId(null);
        setGpsLocations([]);
        setGpsCount(null);
        setSelectedGpsIds([]);
      }
      await fetchGpsList();
    } catch (e) {
      message.error(e?.response?.data?.error || 'Failed to delete dataset');
    } finally {
      setGpsDeletingId(null);
    }
  };

  // Fetch GPS dataset list (saved uploads)
  const fetchGpsList = async () => {
    setGpsFilesLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUserId) params.set('user_id', selectedUserId);
      // If backend supports filtering, e.g. type=gps, uncomment:
      // params.set('type', 'gps');
      const { data } = await axios.get(`${MEDIA_API}${params.toString() ? `?${params.toString()}` : ''}`, { headers: authHeader() });
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setGpsFiles(list);
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load GPS datasets');
    } finally {
      setGpsFilesLoading(false);
    }
  };

  // Optional: try to preview server-parsed GPS file if backend provides it
  const fetchGpsPreview = async (fileId) => {
    if (!fileId) return;
    try {
      const { data } = await axios.get(`${MEDIA_API}/${fileId}/preview`, { headers: authHeader() });
      if (Array.isArray(data?.locations)) {
        const norm = data.locations.map((it, idx) => ({
          id: String(it.id ?? idx + 1),
          address: it.address || it.location || 'Unknown location',
          durationMin: typeof it.durationMin === 'number' ? it.durationMin : Number(it.duration_minutes || 0),
          visitAt: it.visitAt ? dayjs(it.visitAt) : (it.date ? dayjs(it.date) : null),
        }));
        setGpsLocations(norm);
        setGpsCount(norm.length);
        return true;
      }
    } catch (e) {
      // Silently ignore if not supported
    }
    return false;
  };

  // Fallback: download the saved GPS file and parse locally (expects CSV)
  const fetchAndParseGpsFromFile = async (fileId) => {
    if (!fileId) return;
    try {
      // Use the new download endpoint
      const res = await axios.get(`${MEDIA_API}/${fileId}`, { responseType: 'blob', headers: authHeader() });
      const blob = res?.data;
      if (!blob) return;
      const text = await blob.text();
      const parsed = parseCsvTextToLocations(text);
      setGpsLocations(parsed);
      setGpsCount(parsed.length);
    } catch (e) {
      message.error('Failed to load GPS file contents');
    }
  };

  // Function to fetch addresses from Geotab GPS
  const fetchGeotabAddresses = async () => {
    try {
      setFetchingAddresses(true);
      const query = new URLSearchParams();
      query.set('limit', '100');
      query.set('maxAddresses', String(maxAddresses));
      if (Array.isArray(selectedDatabases) && selectedDatabases.length > 0) {
        query.set('databases', selectedDatabases.join(','));
      } else {
        query.set('all', 'true');
      }
      if (Array.isArray(geotabDateRange) && geotabDateRange[0] && geotabDateRange[1]) {
        query.set('startDate', geotabDateRange[0].startOf('day').toISOString());
        query.set('endDate', geotabDateRange[1].endOf('day').toISOString());
      }
      if (typeof stopDurationMin === 'number' && stopDurationMin >= 0) {
        query.set('minStopMinutes', String(Math.floor(stopDurationMin)));
      }
      if (typeof stopDurationMax === 'number' && stopDurationMax >= 0) {
        query.set('maxStopMinutes', String(Math.floor(stopDurationMax)));
      }
      const response = await axios.get(`/api/integrations/geotab/gps-addresses?${query.toString()}`, {
        headers: authHeader()
      });
      
      if (response.data && response.data.addresses) {
        const formattedAddresses = response.data.addresses.map((addr, index) => {
          const stopStart = addr.stopStart ? dayjs(addr.stopStart) : (addr.dateTime ? dayjs(addr.dateTime) : null);
          const stopEnd = addr.stopEnd ? dayjs(addr.stopEnd) : stopStart;
          const stopDurationMinutes = typeof addr.stopDurationMinutes === 'number' ? addr.stopDurationMinutes : null;
          const visitMoment = stopEnd || stopStart || (addr.dateTime ? dayjs(addr.dateTime) : null);
          return {
            id: addr.id || `geotab_${index}`,
            address: addr.address,
            latitude: addr.latitude,
            longitude: addr.longitude,
            city: addr.city,
            state: addr.state,
            country: addr.country,
            postcode: addr.postcode,
            dateTime: addr.dateTime,
            stopStart,
            stopEnd,
            stopDurationMinutes,
            stopOccurrences: addr.stopOccurrences || 1,
            device: addr.device,
            database: addr.database,
            durationMin: stopDurationMinutes ?? 0,
            visitAt: visitMoment,
            zoneType: 'Geotab GPS Location'
          };
        });
        
        setGeotabAddresses(formattedAddresses);
        setAddressesFetched(true);
        message.success(`Fetched ${formattedAddresses.length} addresses from Geotab GPS`);
      }
    } catch (error) {
      console.error('Error fetching Geotab addresses:', error);
      message.error(error?.response?.data?.error || 'Failed to fetch addresses from Geotab GPS');
    } finally {
      setFetchingAddresses(false);
    }
  };

  // Function to use selected Geotab addresses as GPS locations
  const useSelectedGeotabAddresses = () => {
    if (selectedGeotabIds.length > 0) {
      const selectedAddresses = geotabAddresses.filter(addr => selectedGeotabIds.includes(addr.id));
      setGpsLocations(selectedAddresses);
      setGpsCount(selectedAddresses.length);
      setSelectedGpsIds(selectedAddresses.map(a => a.id));
      // advance to Locations step (SuperAdmin step index: 4)
      setStep(4);
      message.success(`Loaded ${selectedAddresses.length} selected locations from Geotab GPS data`);
    } else {
      message.warning('Please select at least one address to use');
    }
  };

  // Function to use all Geotab addresses as GPS locations
  const useAllGeotabAddresses = () => {
    if (geotabAddresses.length > 0) {
      setGpsLocations(geotabAddresses);
      setGpsCount(geotabAddresses.length);
      setSelectedGeotabIds(geotabAddresses.map(addr => addr.id));
      setSelectedGpsIds(geotabAddresses.map(addr => addr.id));
      // advance to Locations step (SuperAdmin step index: 4)
      setStep(4);
      message.success(`Loaded all ${geotabAddresses.length} locations from Geotab GPS data`);
    }
  };

  // Filter addresses based on city, state, zipcode
  const filteredGeotabAddresses = geotabAddresses.filter(addr => {
    const cityMatch = !filterCity || (addr.city && addr.city.toLowerCase().includes(filterCity.toLowerCase()));
    const stateMatch = !filterState || (addr.state && addr.state.toLowerCase().includes(filterState.toLowerCase()));
    const zipcodeMatch = !filterZipcode || (addr.postcode && addr.postcode.includes(filterZipcode));
    const duration = typeof addr.stopDurationMinutes === 'number' ? addr.stopDurationMinutes : addr.durationMin || 0;
    const minOk = typeof stopDurationMin === 'number' ? duration >= stopDurationMin : true;
    const maxOk = typeof stopDurationMax === 'number' ? duration <= stopDurationMax : true;
    return cityMatch && stateMatch && zipcodeMatch && minOk && maxOk;
  });

  const resetGeotabFetchFilters = () => {
    setGeotabDateRange([dayjs().subtract(7, 'day'), dayjs()]);
    setStopDurationMin(0);
    setStopDurationMax(null);
  };

  // Get unique values for filter dropdowns
  const uniqueCities = [...new Set(geotabAddresses.map(addr => addr.city).filter(Boolean))].sort();
  const uniqueStates = [...new Set(geotabAddresses.map(addr => addr.state).filter(Boolean))].sort();
  const uniqueZipcodes = [...new Set(geotabAddresses.map(addr => addr.postcode).filter(Boolean))].sort();

  // Clear filters function
  const clearFilters = () => {
    setFilterCity('');
    setFilterState('');
    setFilterZipcode('');
  };

  // CSV parsing utilities (robust splitting + specific headers for GPS: Stop Duration, Location, Stop Zone Types)
  const csvSplit = (line) => {
    const result = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i+1] === '"') { // escaped quote
          cur += '"'; i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(cur);
        cur = '';
      } else {
        cur += ch;
      }
    }
    result.push(cur);
    return result;
  };

  // Parse Audience Profiles CSV (columns D,E,F,G => Street, City, State, Zip) with fallbacks to header names
  const parseProfilesCsvText = (text) => {
    const lines = String(text || '').split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return [];
    const header = csvSplit(lines[0]).map(h => String(h||'').trim());
    const headerLc = header.map(h => h.toLowerCase());
    // Prefer exact positions D-G (3..6), but also try header matching when available
    let streetIdx = 3, cityIdx = 4, stateIdx = 5, zipIdx = 6;
    let emailIdx = -1, phoneIdx = -1;
    
    const idxStreet = headerLc.findIndex(h => /(street|address(\s*line)?\s*1)/.test(h));
    const idxCity = headerLc.findIndex(h => /city/.test(h));
    const idxState = headerLc.findIndex(h => /state/.test(h));
    const idxZip = headerLc.findIndex(h => /(zip|postal)/.test(h));
    const idxEmail = headerLc.findIndex(h => /(email|e-mail|mail)/.test(h));
    const idxPhone = headerLc.findIndex(h => /(phone|tel|mobile|cell)/.test(h));
    
    if (idxStreet !== -1) streetIdx = idxStreet;
    if (idxCity !== -1) cityIdx = idxCity;
    if (idxState !== -1) stateIdx = idxState;
    if (idxZip !== -1) zipIdx = idxZip;
    if (idxEmail !== -1) emailIdx = idxEmail;
    if (idxPhone !== -1) phoneIdx = idxPhone;

    const rows = lines.slice(1);
    const out = [];
    rows.forEach((row, i) => {
      const cols = csvSplit(row);
      const street = cols[streetIdx] ? String(cols[streetIdx]).trim() : '';
      const city = cols[cityIdx] ? String(cols[cityIdx]).trim() : '';
      const state = cols[stateIdx] ? String(cols[stateIdx]).trim() : '';
      const zip = cols[zipIdx] ? String(cols[zipIdx]).trim() : '';
      const email = emailIdx !== -1 && cols[emailIdx] ? String(cols[emailIdx]).trim() : '';
      const phone = phoneIdx !== -1 && cols[phoneIdx] ? String(cols[phoneIdx]).trim() : '';
      const address = [street, city, state, zip].filter(Boolean).join(', ');
      if (address) { // Only add if address is not empty
        out.push({ id: String(i + 1), address, street, city, state, zip, email, phone });
      }
    });
    return out;
  };

  // Download and parse an audience file (CSV) for preview/selection/results
  const fetchAndParseAudienceFile = async (fileId) => {
    if (!fileId) return;
    try {
      // Use the new download endpoint
      const res = await axios.get(`${MEDIA_API}/${fileId}`, { responseType: 'blob', headers: authHeader() });
      const blob = res?.data;
      if (!blob) {
        return [];
      }
      const text = await blob.text();
      const parsed = parseProfilesCsvText(text);
      
      // Standardize addresses using geocoding (like Geotab format)
      setStandardizingAddresses(true);
      setAddressesStandardized(false);
      try {
        const standardized = await standardizeProfileAddresses(parsed);
        setProfiles(standardized);
        setProfilesCount(standardized.length);
        setAddressesStandardized(true);
        message.success(`Parsed and standardized ${standardized.length} profile addresses`);
        return standardized;
      } catch (e) {
        console.error('Address standardization failed:', e);
        setProfiles(parsed);
        setProfilesCount(parsed.length);
        message.warning('Profiles loaded but address standardization failed');
        return parsed;
      } finally {
        setStandardizingAddresses(false);
      }
    } catch (e) {
      message.error('Failed to load audience file contents');
      return [];
    }
  };

  const parseCsvTextToLocations = (text) => {
    const lines = String(text || '').split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return [];
    const rawHeader = csvSplit(lines[0]);
    const header = rawHeader.map(h => h.trim());
    const headerLc = header.map(h => h.toLowerCase());

    // Prefer exact column names first
    let addrIdx = headerLc.indexOf('location');
    let durIdx = headerLc.indexOf('stop duration');
    let zoneIdx = headerLc.indexOf('stop zone types');
    // Fallbacks if exact headers are not present
    if (addrIdx === -1) addrIdx = headerLc.findIndex(h => /address|location/.test(h));
    if (durIdx === -1) durIdx = headerLc.findIndex(h => /stop\s*duration|duration|mins|minutes|time\s*spent/.test(h));
    if (zoneIdx === -1) zoneIdx = headerLc.findIndex(h => /stop\s*zone\s*types|zone\s*type/.test(h));

    const rows = lines.slice(1);
    const out = [];
    rows.forEach((row, i) => {
      const cols = csvSplit(row);
      const address = addrIdx > -1 && cols[addrIdx] != null && String(cols[addrIdx]).trim() !== ''
        ? String(cols[addrIdx]).trim()
        : `Location ${i+1}`;
      const rawDur = durIdx > -1 && cols[durIdx] != null ? String(cols[durIdx]).trim() : '';
      const zone = zoneIdx > -1 && cols[zoneIdx] != null ? String(cols[zoneIdx]).trim() : '';
      out.push({
        id: String(i + 1),
        address,
        durationMin: parseDurationToMinutes(rawDur),
        visitAt: null,
        zoneType: zone,
      });
    });
    return out;
  };

  const parseDurationToMinutes = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return Math.max(0, Math.floor(val));
    const s = String(val).trim();
    if (!s) return 0;
    // Try H:MM or M:SS
    if (/^\d{1,2}:\d{1,2}$/.test(s)) {
      const [a, b] = s.split(':').map(n => parseInt(n, 10) || 0);
      // Assume hours:minutes if first part > 23 then it's minutes:seconds -> convert to minutes
      if (a <= 23) return a * 60 + b;
      return Math.floor(a + b / 60);
    }
    // Try like "48:14 (min:sec)" extract first time
    const m = s.match(/(\d{1,2}):(\d{2})/);
    if (m) {
      const a = parseInt(m[1], 10) || 0;
      const b = parseInt(m[2], 10) || 0;
      if (a <= 23) return a * 60 + b;
      return Math.floor(a + b / 60);
    }
    // Try "120 min" or just a number
    const num = parseFloat(s.replace(/[^0-9.]/g, ''));
    if (!isNaN(num)) return Math.floor(num);
    return 0;
  };

  const minutesToHHMM = (mins) => {
    const m = Math.max(0, Math.floor(mins));
    const h = Math.floor(m / 60);
    const mm = String(m % 60).padStart(2, '0');
    return `${h}:${mm}`;
  };

  const filteredGps = gpsLocations;

  useEffect(() => {
    // Reset selections when the locations list changes
    setSelectedGpsIds([]);
  }, [gpsLocations]);

  const fetchCampaigns = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset: (page - 1) * pageSize });
      const { data: res } = await axios.get(`${API_BASE_URL}?${params.toString()}`, { headers: authHeader() });
      setData(res.items || []);
      setPagination({ current: page, pageSize, total: res.count ?? (res.items?.length || 0) });
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
    
  };

  useEffect(() => {
    fetchCampaigns(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load lists when entering steps
  useEffect(() => {
    if (step === 2) {
      fetchMediaList();
    } else if (step === 3) {
      fetchGpsList();
      fetchGeotabAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedUserId]);

  const onTableChange = (pag) => {
    fetchCampaigns(pag.current, pag.pageSize);
  };

  const authHeader = () => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Auto-populate results when reaching step 5 and profiles exist
  useEffect(() => {
    if (step === 5 && profiles && profiles.length > 0 && resultsProfiles.length === 0) {
      console.log('Auto-populating results with existing profiles');
      setResultsProfiles(profiles);
      setResultsSelectedIds([]); // Start with no profiles selected
      setResultsCurrentPage(1); // Reset to first page
    }
  }, [step, profiles, resultsProfiles.length]);

  // Debug log for Facebook wizard data
  useEffect(() => {
    if (fbWizardOpen) {
      console.log('Facebook wizard opened with data:');
      console.log('- resultsSelectedIds:', resultsSelectedIds);
      console.log('- resultsProfiles length:', resultsProfiles.length);
      console.log('- profiles length:', profiles.length);
      console.log('- adType:', adType);
    }
  }, [fbWizardOpen, resultsSelectedIds, resultsProfiles.length, profiles.length, adType]);

  // Geocode address to get coordinates and standardized format (like Geotab)
  const geocodeAddress = async (address) => {
    if (!address || !address.trim()) return null;
    try {
      const params = new URLSearchParams({ q: address.trim(), limit: 1 });
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const { data } = await axios.get(`${ADDRESS_SEARCH_API}?${params.toString()}`, { 
        headers: authHeader(),
        signal: controller.signal,
        timeout: 10000
      });
      
      clearTimeout(timeoutId);
      const items = Array.isArray(data?.items) ? data.items : [];
      if (items.length > 0) {
        const item = items[0];
        return {
          originalAddress: address,
          standardizedAddress: item.display_name || address,
          latitude: item.lat,
          longitude: item.lon,
          coordinates: `${item.lat},${item.lon}`,
        };
      }
    } catch (e) {
      console.warn('Geocoding failed for address:', address, e.name === 'AbortError' ? 'Timeout' : e.message);
    }
    return {
      originalAddress: address,
      standardizedAddress: address,
      latitude: null,
      longitude: null,
      coordinates: null,
    };
  };

  // Standardize all addresses in profiles using geocoding
  const standardizeProfileAddresses = async (profiles) => {
    if (!Array.isArray(profiles) || profiles.length === 0) return profiles;
    
    const standardized = [];
    const batchSize = 3; // Smaller batches for better responsiveness
    const maxProfiles = Math.min(profiles.length, 50); // Limit to 50 profiles to prevent hanging
    const profilesToProcess = profiles.slice(0, maxProfiles);
    
    console.log(`Standardizing ${profilesToProcess.length} profiles (limited from ${profiles.length})`);
    
    for (let i = 0; i < profilesToProcess.length; i += batchSize) {
      const batch = profilesToProcess.slice(i, i + batchSize);
      console.log(`Processing batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(profilesToProcess.length/batchSize)}`);
      
      const batchPromises = batch.map(async (profile) => {
        try {
          const geocoded = await geocodeAddress(profile.address);
          return {
            ...profile,
            originalAddress: profile.address,
            address: geocoded.standardizedAddress,
            standardizedAddress: geocoded.standardizedAddress,
            latitude: geocoded.latitude,
            longitude: geocoded.longitude,
            coordinates: geocoded.coordinates,
          };
        } catch (e) {
          console.warn('Failed to geocode profile:', profile.id, e);
          return {
            ...profile,
            originalAddress: profile.address,
            standardizedAddress: profile.address,
            latitude: null,
            longitude: null,
            coordinates: null,
          };
        }
      });
      
      const batchResults = await Promise.all(batchPromises);
      standardized.push(...batchResults);
      
      // Small delay between batches to be respectful to the geocoding service
      if (i + batchSize < profilesToProcess.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    // Add remaining profiles without geocoding if we hit the limit
    if (profiles.length > maxProfiles) {
      const remaining = profiles.slice(maxProfiles).map(profile => ({
        ...profile,
        originalAddress: profile.address,
        standardizedAddress: profile.address,
        latitude: null,
        longitude: null,
        coordinates: null,
      }));
      standardized.push(...remaining);
      console.log(`Added ${remaining.length} profiles without geocoding due to limit`);
    }
    
    return standardized;
  };

  // Fetch users for Step 1
  const fetchUsers = async (q = '') => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      const { data } = await axios.get(`${USERS_API}${params.toString() ? `?${params.toString()}` : ''}` , { headers: authHeader() });
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setUsers(list);
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setSelectedUserId(null);
    setAdType(null);
    setStep(0);
    setIsModalVisible(true);
    fetchUsers('');
    setAudienceFiles([]);
    setSelectedAudienceFileId(null);
    setProfilesCount(null);
    setProfiles([]);
    setStandardizingAddresses(false);
    setAddressesStandardized(false);
    setResultsProfiles([]);
    setResultsSelectedIds([]);
    setResultsCurrentPage(1);
  };

  const onEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      client_id: record.client_id,
      zone_id: record.zone_id,
      start_date: record.start_date ? dayjs(record.start_date) : null,
      end_date: record.end_date ? dayjs(record.end_date) : null,
      budget: record.budget,
      status: record.status,
      agency_id: record.agency_id,
    });
    setIsModalVisible(true);
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/${id}`, { headers: authHeader() });
      message.success('Campaign deleted');
      fetchCampaigns(pagination.current, pagination.pageSize);
    } catch (e) {
      message.error(e.response?.data?.error || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        client_id: (selectedUserId ?? values.client_id) ?? null,
        zone_id: values.zone_id ?? null,
        start_date: values.start_date?.format ? values.start_date.format('YYYY-MM-DD') : values.start_date || null,
        end_date: values.end_date?.format ? values.end_date.format('YYYY-MM-DD') : values.end_date || null,
        budget: values.budget ?? null,
        status: values.status,
        agency_id: values.agency_id ?? null,
        // ad_type is for future backend support
        ad_type: adType || null,
        audience_file_id: selectedAudienceFileId || null,
      };

      if (editingId) {
        await axios.put(`${API_BASE_URL}/${editingId}`, payload, { headers: authHeader() });
        message.success('Campaign updated');
      } else {
        await axios.post(`${API_BASE_URL}`, payload, { headers: authHeader() });
        message.success('Campaign created');
      }
      setIsModalVisible(false);
      fetchCampaigns(pagination.current, pagination.pageSize);
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error(e.response?.data?.error || 'Save failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Campaigns' },
        { title: 'All Campaigns' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>All Campaigns</Title>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Campaign</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Manage campaigns. Create, edit, or delete entries.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={pagination}
          onChange={onTableChange}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Campaign' : 'New Campaign'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        destroyOnClose
        width={920}
      >
        {/* Steps header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, background: '#f1f5f9', borderRadius: 8, padding: 6, marginBottom: 16 }}>
          {['Select Client','Ad Type','Upload Profiles','Upload GPS','Locations','Results'].map((label, idx) => (
            <div key={label} style={{ background: idx===step ? 'white' : 'transparent', borderRadius: 6, padding: '8px 10px', textAlign: 'center', fontWeight: idx===step ? 600 : 500, color: idx>step ? '#94a3b8' : '#0f172a', border: idx===step ? '1px solid #e2e8f0' : '1px solid transparent' }}>{label}</div>
          ))}
        </div>

        {/* Step 0: Select Client */}
        {step === 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600 }}>Select Client</div>
              <div style={{ color: '#64748b' }}>Selected: {selectedUserId ? 1 : 0}</div>
            </div>
            <Input
              placeholder="Search users..."
              value={userSearch}
              onChange={(e)=>{ setUserSearch(e.target.value); fetchUsers(e.target.value); }}
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, maxHeight: 360, overflowY: 'auto' }}>
              {usersLoading ? <div>Loading users...</div> : (
                users.map(u => (
                  <div key={u.id}
                    onClick={()=> setSelectedUserId(u.id)}
                    style={{ cursor:'pointer', border: selectedUserId===u.id? '2px solid #1677ff' : '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#fff' }}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:32, height:32, borderRadius:16, background:'#e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{String(u.full_name||u.email||'U').trim()[0]?.toUpperCase()}</div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:600, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden' }}>{u.full_name || u.email || 'Unnamed'}</div>
                        <div style={{ color:'#64748b', fontSize:12, whiteSpace:'nowrap', textOverflow:'ellipsis', overflow:'hidden' }}>{u.email || ''}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
              <Button onClick={()=> setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" disabled={!selectedUserId} onClick={()=> setStep(1)}>Next: Ad Type</Button>
            </div>
          </div>
        )}

        {/* Step 1: Ad Type */}
        {step === 1 && (
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Select Ad Type</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div onClick={()=> setAdType('NeighborhoodLens')} style={{ cursor:'pointer', border: adType==='NeighborhoodLens'? '2px solid #1677ff' : '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 600 }}>Create NeighborhoodLens Ad</div>
                <div style={{ color:'#64748b', marginTop: 6 }}>Target audiences based on their proximity to specific locations. Upload audience profiles and GPS data to create location-based targeting.</div>
                <div style={{ marginTop: 8 }}>
                  <Tag>Audience Profiles</Tag>
                  <Tag>GPS Targeting</Tag>
                  <Tag>Proximity Filtering</Tag>
                </div>
              </div>
              <div onClick={()=> setAdType('Geofence')} style={{ cursor:'pointer', border: adType==='Geofence'? '2px solid #1677ff' : '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 600 }}>Create Geofence Ad</div>
                <div style={{ color:'#64748b', marginTop: 6 }}>Target users within specific geographic areas. Upload GPS locations to create geofenced ad campaigns with custom radius settings.</div>
                <div style={{ marginTop: 8 }}>
                  <Tag>Location Targeting</Tag>
                  <Tag>Radius Control</Tag>
                  <Tag>Geographic Fencing</Tag>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
              <Button onClick={()=> setStep(0)}>Previous: Select Client</Button>
              <Space>
                <Button onClick={()=> setIsModalVisible(false)}>Cancel</Button>
                <Button type="primary" disabled={!adType} onClick={()=> {
                  // Prefill form values for downstream steps
                  form.setFieldsValue({ client_id: selectedUserId });
                  const nextStep = adType === 'NeighborhoodLens' ? 2 : 3;
                  setStep(nextStep);
                  message.success('Client and Ad Type selected');
                }}>
                  {adType === 'Geofence' ? 'Next: Upload GPS' : 'Next: Upload Profiles'}
                </Button>
              </Space>
            </div>
          </div>
        )}

        {/* Step 2: Upload Profiles (NeighborhoodLens) */}
        {step === 2 && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Upload Audience Profiles</div>
            <div style={{ background: '#eaf2ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 14 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Admin Direct Upload</div>
              <div style={{ color: '#475569', marginBottom: 12 }}>As an admin, you can upload audience profile data directly for campaign creation. <b>Address data must be in columns D, E, F, G (Street, City, State, Zip)</b> and all address fields must be complete.</div>
              <Upload.Dragger
                name="file"
                multiple={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    // Count CSV rows (client-side) for feedback
                    const tryCountCsv = async (f) => new Promise((resolve) => {
                      if (!f || (!f.type?.includes('csv') && !f.name?.toLowerCase().endsWith('.csv'))) {
                        return resolve(null);
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        const text = String(reader.result || '');
                        const lines = text.split(/\r?\n/).map(l=>l.trim()).filter(Boolean);
                        const count = Math.max(0, lines.length - 1);
                        resolve(count);
                      };
                      reader.onerror = () => resolve(null);
                      reader.readAsText(f);
                    });
                    setUploadingProfiles(true);
                    const count = await tryCountCsv(file);
                    if (count !== null) setProfilesCount(count);
                    const fd = new FormData();
                    fd.append('file', file);
                    const { data } = await axios.post('/api/media', fd, { headers: { ...authHeader() } });
                    // Refresh list and select uploaded
                    await fetchMediaList();
                    const newId = data?.id;
                    if (newId) {
                      setSelectedAudienceFileId(newId);
                      // Parse and standardize addresses immediately
                      await fetchAndParseAudienceFile(newId);
                    }
                    message.success('Audience file uploaded and addresses standardized');
                    onSuccess && onSuccess(data, file);
                  } catch (e) {
                    message.error(e?.response?.data?.error || e?.message || 'Upload failed');
                    onError && onError(e);
                  } finally {
                    setUploadingProfiles(false);
                  }
                }}
                showUploadList={false}
                accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={uploadingProfiles || standardizingAddresses}
                style={{ borderRadius: 8 }}
              >
                <p>
                  <Button type="primary" loading={uploadingProfiles || standardizingAddresses}>
                    {standardizingAddresses ? 'Standardizing Addresses...' : 'Upload Audience Profiles'}
                  </Button>
                </p>
                <p style={{ color: '#64748b' }}>Upload CSV or Excel file with customer profile data</p>
                <p style={{ color: '#64748b' }}>
                  Required: Address data in columns D (Street), E (City), F (State), G (Zip)
                </p>
              </Upload.Dragger>
              {profilesCount !== null && (
                <div style={{ marginTop: 12 }}>
                  <Alert 
                    type="success" 
                    showIcon 
                    message="Audience Data Loaded" 
                    description={`${profilesCount} profiles ready for processing`} 
                  />
                  {standardizingAddresses && (
                    <Alert 
                      style={{ marginTop: 8 }} 
                      type="info" 
                      showIcon 
                      message="Standardizing Addresses" 
                      description="Converting addresses to Geotab format using coordinates..." 
                    />
                  )}
                  {addressesStandardized && !standardizingAddresses && (
                    <Alert 
                      style={{ marginTop: 8 }} 
                      type="success" 
                      showIcon 
                      message="Addresses Standardized" 
                      description="All addresses have been converted to standardized Geotab format with coordinates" 
                    />
                  )}
                </div>
              )}
            </div>

            <Divider>OR</Divider>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Select from Existing User Files</div>
              <Select
                placeholder="Available Audience Files"
                value={selectedAudienceFileId}
                onChange={async (fileId) => {
                  setSelectedAudienceFileId(fileId);
                  if (fileId) {
                    // Parse and standardize addresses when selecting existing file
                    await fetchAndParseAudienceFile(fileId);
                  }
                }}
                loading={audienceFilesLoading || standardizingAddresses}
                style={{ width: '100%' }}
                options={(audienceFiles||[]).map(f => ({ value: f.id, label: `${f.id} — ${f.original_name || f.name} (${((f.size||0)/(1024*1024)).toFixed(2)} MB)` }))}
              />
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
              <Button onClick={()=> setStep(1)}>Previous: Ad Type</Button>
              <Button type="primary" disabled={!selectedAudienceFileId || standardizingAddresses} onClick={()=> setStep(3)}>Next: Upload GPS Data</Button>
            </div>
          </div>
        )}

        {/* Step 3: Upload GPS */}
        {step === 3 && (
          <div>
            {/* Get Address from Geotab GPS - Only show if Geotab credentials are available */}
            {geotabDbOptions.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Get Addresses from Geotab GPS</div>
              <div style={{ background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Fetch GPS Coordinates from Geotab</div>
                <div style={{ color: '#475569', marginBottom: 12 }}>
                  Automatically fetch GPS coordinates from your Geotab account and convert them to readable addresses. 
                  This will retrieve recent GPS data from your fleet and convert coordinates to street addresses.
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Number of addresses to fetch:</label>
                  <Select
                    value={maxAddresses}
                    onChange={setMaxAddresses}
                    style={{ width: 200 }}
                    options={[
                      { value: 25, label: '25 addresses (faster)' },
                      { value: 50, label: '50 addresses (recommended)' },
                      { value: 75, label: '75 addresses' },
                      { value: 100, label: '100 addresses (slower)' },
                      { value: 'all', label: 'All addresses' }
                    ]}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Databases to query:</label>
                  <Select
                    mode="multiple"
                    placeholder="Select databases (default: all)"
                    value={selectedDatabases}
                    onChange={setSelectedDatabases}
                    allowClear
                    style={{ minWidth: 260 }}
                    options={geotabDbOptions}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Date range:</label>
                    <DatePicker.RangePicker
                      value={geotabDateRange}
                      onChange={(values) => {
                        if (!values || values.length !== 2) {
                          setGeotabDateRange([dayjs().subtract(7, 'day'), dayjs()]);
                          return;
                        }
                        const [start, end] = values;
                        if (!start || !end) {
                          setGeotabDateRange([dayjs().subtract(7, 'day'), dayjs()]);
                        } else {
                          setGeotabDateRange([start, end]);
                        }
                      }}
                      allowClear={false}
                      disabledDate={(current) => current && current > dayjs().endOf('day')}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Stop duration (minutes):</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <InputNumber
                        min={0}
                        placeholder="Min"
                        value={stopDurationMin}
                        onChange={(value) => {
                          setStopDurationMin(typeof value === 'number' ? value : 0);
                          if (typeof stopDurationMax === 'number' && value != null && value > stopDurationMax) {
                            setStopDurationMax(value);
                          }
                        }}
                        style={{ width: '100%' }}
                      />
                      <InputNumber
                        min={0}
                        placeholder="Max"
                        value={stopDurationMax}
                        onChange={(value) => {
                          if (value == null) {
                            setStopDurationMax(null);
                            return;
                          }
                          if (typeof value === 'number' && value >= 0) {
                            setStopDurationMax(value);
                            if (typeof stopDurationMin === 'number' && value < stopDurationMin) {
                              setStopDurationMin(value);
                            }
                          }
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <Button size="small" onClick={resetGeotabFetchFilters}>Reset Date & Duration Filters</Button>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button 
                    type="primary" 
                    loading={fetchingAddresses}
                    onClick={fetchGeotabAddresses}
                    icon={<span>📍</span>}
                  >
                    Get Address
                  </Button>
                  {addressesFetched && geotabAddresses.length > 0 && (
                    <>
                      <Button 
                        onClick={useSelectedGeotabAddresses}
                        disabled={selectedGeotabIds.length === 0}
                        style={{ background: '#10b981', borderColor: '#10b981', color: 'white' }}
                      >
                        Use Selected ({selectedGeotabIds.length} locations)
                      </Button>
                      <Button 
                        onClick={useAllGeotabAddresses}
                        type="default"
                      >
                        Use All ({geotabAddresses.length} locations)
                      </Button>
                    </>
                  )}
                </div>
                
                {fetchingAddresses && (
                  <Alert 
                    style={{ marginTop: 12 }} 
                    type="info" 
                    showIcon 
                    message="Fetching GPS Data" 
                    description="Retrieving coordinates from Geotab and converting to addresses..." 
                  />
                )}
                
                {addressesFetched && (
                  <Alert 
                    style={{ marginTop: 12 }} 
                    type="success" 
                    showIcon 
                    message="Addresses Retrieved" 
                    description={`Successfully fetched ${geotabAddresses.length} addresses from Geotab GPS data`} 
                  />
                )}
              </div>

              {/* Address Filters */}
              {geotabAddresses.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16, background: '#fafafa' }}>
                  <div style={{ fontWeight: 600, marginBottom: 12 }}>Filter Addresses</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>City:</label>
                      <Select
                        value={filterCity}
                        onChange={setFilterCity}
                        placeholder="Filter by city"
                        allowClear
                        style={{ width: '100%' }}
                        showSearch
                        options={uniqueCities.map(city => ({ value: city, label: city }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>State:</label>
                      <Select
                        value={filterState}
                        onChange={setFilterState}
                        placeholder="Filter by state"
                        allowClear
                        style={{ width: '100%' }}
                        showSearch
                        options={uniqueStates.map(state => ({ value: state, label: state }))}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Zipcode:</label>
                      <Select
                        value={filterZipcode}
                        onChange={setFilterZipcode}
                        placeholder="Filter by zipcode"
                        allowClear
                        style={{ width: '100%' }}
                        showSearch
                        options={uniqueZipcodes.map(zip => ({ value: zip, label: zip }))}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Showing {filteredGeotabAddresses.length} of {geotabAddresses.length} addresses
                    </div>
                    <Button size="small" onClick={clearFilters}>Clear Filters</Button>
                  </div>
                </div>
              )}

              {/* Display fetched addresses with selection */}
              {geotabAddresses.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600 }}>
                      {filteredGeotabAddresses.length < geotabAddresses.length 
                        ? `Filtered Addresses (${filteredGeotabAddresses.length} of ${geotabAddresses.length} locations)`
                        : `Fetched Addresses (${geotabAddresses.length} locations)`
                      }
                    </div>
                    <Button 
                      size="small"
                      onClick={() => {
                        const filteredIds = filteredGeotabAddresses.map(addr => addr.id);
                        const allFilteredSelected = filteredIds.every(id => selectedGeotabIds.includes(id));
                        if (allFilteredSelected) {
                          // Deselect all filtered addresses
                          setSelectedGeotabIds(prev => prev.filter(id => !filteredIds.includes(id)));
                        } else {
                          // Select all filtered addresses
                          setSelectedGeotabIds(prev => [...new Set([...prev, ...filteredIds])]);
                        }
                      }}
                    >
                      {filteredGeotabAddresses.every(addr => selectedGeotabIds.includes(addr.id)) ? 'Deselect Filtered' : 'Select Filtered'}
                    </Button>
                  </div>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {filteredGeotabAddresses.map((addr, index) => (
                      <div key={addr.id} style={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        gap: 10, 
                        padding: 8, 
                        borderBottom: index < filteredGeotabAddresses.length - 1 ? '1px solid #f1f5f9' : 'none',
                        background: selectedGeotabIds.includes(addr.id) ? '#f0f9ff' : 'transparent'
                      }}>
                        <Checkbox
                          checked={selectedGeotabIds.includes(addr.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedGeotabIds(prev => 
                              checked 
                                ? [...new Set([...prev, addr.id])]
                                : prev.filter(id => id !== addr.id)
                            );
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>{addr.address}</div>
                          <div style={{ color: '#64748b', fontSize: 12 }}>
                            Device: {addr.device} • Coordinates: {addr.latitude?.toFixed(6)}, {addr.longitude?.toFixed(6)}
                            {addr.dateTime && ` • ${dayjs(addr.dateTime).format('M/D/YYYY h:mm A')}`}
                          </div>
                          <div style={{ color: '#475569', fontSize: 12 }}>
                            Stop Duration: {typeof addr.stopDurationMinutes === 'number' ? `${addr.stopDurationMinutes} min` : 'N/A'}
                            {addr.stopStart && ` • Start: ${dayjs(addr.stopStart).format('M/D/YYYY h:mm A')}`}
                            {addr.stopEnd && ` • End: ${dayjs(addr.stopEnd).format('M/D/YYYY h:mm A')}`}
                            {addr.stopOccurrences && addr.stopOccurrences > 1 && ` • Occurrences: ${addr.stopOccurrences}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6, fontSize: 12, color: '#64748b' }}>
                    Selected: {selectedGeotabIds.length} total • Showing: {filteredGeotabAddresses.length} addresses
                    {filteredGeotabAddresses.length < geotabAddresses.length && (
                      <span style={{ marginLeft: 8, color: '#f59e0b' }}>
                        (Filtered from {geotabAddresses.length} total)
                      </span>
                    )}
                  </div>
                </div>
              )}
              </div>
            )}

            <Divider>OR</Divider>

            {/* Saved datasets and upload */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>GPS Data</div>
              <div style={{ color: '#64748b', marginBottom: 8 }}>Upload new GPS data or select from previously saved datasets.</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {(gpsFiles||[]).map(f => {
                  const selected = selectedGpsFileId === f.id;
                  return (
                    <div
                      key={f.id}
                      onClick={async () => {
                        setSelectedGpsFileId(f.id);
                        const ok = await fetchGpsPreview(f.id);
                        if (!ok) await fetchAndParseGpsFromFile(f.id);
                      }}
                      style={{ cursor: 'pointer', border: selected ? '2px solid #1677ff' : '1px solid #e2e8f0', background: selected ? '#eaf2ff' : '#fff', borderRadius: 8, padding: 12, display:'flex', justifyContent:'space-between', alignItems:'center' }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{f.original_name || f.name || `File ${f.id}`}</div>
                        <div style={{ color: '#64748b', fontSize: 12 }}>
                          {(f.location_count || f.count || 0)} locations • {((f.size||0)/(1024*1024)).toFixed(1)}MB • Created {f.created_at ? dayjs(f.created_at).format('M/D/YYYY') : ''}
                        </div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                        {selected && <div style={{ color: '#1677ff', fontWeight: 600 }}>Selected</div>}
                        <Popconfirm
                          title="Delete this GPS dataset?"
                          okText="Delete"
                          okButtonProps={{ danger: true, loading: gpsDeletingId === f.id }}
                          onConfirm={() => deleteGpsFile(f.id)}
                        >
                          <Button size="small" danger loading={gpsDeletingId === f.id} icon={<DeleteOutlined />}>Delete</Button>
                        </Popconfirm>
                      </div>
                    </div>
                  );
                })}
                {gpsFilesLoading && <div>Loading datasets...</div>}
              </div>

              <Divider>Or upload new GPS data</Divider>
              <Upload.Dragger
                name="file"
                multiple={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    setUploadingGps(true);
                    // Client-side parse for preview
                    const reader = new FileReader();
                    const text = await new Promise((res, rej) => {
                      reader.onload = () => res(String(reader.result||''));
                      reader.onerror = rej;
                      reader.readAsText(file);
                    });
                    const parsed = parseCsvTextToLocations(text);
                    setGpsLocations(parsed);
                    setGpsCount(parsed.length);
                    // Upload to server to persist
                    const fd = new FormData();
                    fd.append('file', file);
                    const { data } = await axios.post('/api/media', fd, { headers: { ...authHeader() } });
                    const newId = data?.id;
                    await fetchGpsList();
                    if (newId) {
                      setSelectedGpsFileId(newId);
                    }
                    message.success('GPS file uploaded');
                    onSuccess && onSuccess(data, file);
                  } catch (e) {
                    message.error(e?.response?.data?.error || e?.message || 'Upload failed');
                    onError && onError(e);
                  } finally {
                    setUploadingGps(false);
                  }
                }}
                showUploadList={false}
                accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                disabled={uploadingGps}
                style={{ borderRadius: 8 }}
              >
                <p>
                  <Button type="primary" loading={uploadingGps}>Upload New GPS File</Button>
                </p>
                <p style={{ color: '#64748b' }}>Supported formats: CSV, XLSX, XLS (max 50MB)</p>
              </Upload.Dragger>
            </div>

            {/* Counts and filters */}
            {gpsCount !== null && (
              <Alert style={{ marginBottom: 12 }} type="success" showIcon message="GPS Data Loaded" description={`${gpsCount} locations ready for processing`} />
            )}
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Displaying: {filteredGps.length} locations</div>

            {/* Selectable list */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600 }}>Select GPS Locations ({selectedGpsIds.length} selected)</div>
              <Button onClick={() => {
                const allIds = filteredGps.map(x => x.id);
                const allSelected = allIds.every(id => selectedGpsIds.includes(id));
                setSelectedGpsIds(allSelected ? [] : allIds);
              }}>Select All</Button>
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              {filteredGps.map(item => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', gap:10, padding: 10, borderBottom: '1px solid #f1f5f9' }}>
                  <Checkbox
                    checked={selectedGpsIds.includes(item.id)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedGpsIds(prev => checked ? [...new Set([...prev, item.id])] : prev.filter(id => id !== item.id));
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{item.address}</div>
                    <div style={{ color:'#64748b', fontSize: 12 }}>Duration: {minutesToHHMM(item.durationMin)}</div>
                    {item.zoneType && <div style={{ color:'#64748b', fontSize: 12 }}>Stop Zone Types: {item.zoneType}</div>}
                  </div>
                </div>
              ))}
              {filteredGps.length === 0 && (
                <div style={{ padding: 12, color:'#64748b' }}>No locations match the current filters.</div>
              )}
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
              <Button onClick={()=> setStep(2)}>Previous: Upload Profiles</Button>
              <Button type="primary" disabled={selectedGpsIds.length === 0} onClick={()=> { form.setFieldsValue({ gps_location_ids: selectedGpsIds }); setStep(4); }}>Next: Set Locations ({selectedGpsIds.length} selected)</Button>
            </div>
          </div>
        )}

        {/* Step 4: Locations (Geofence Settings) */}
        {step === 4 && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Configure Geofence Settings</div>
            <div style={{ color: '#64748b', marginBottom: 12 }}>Configure the radius for your geofence targeting areas</div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Quick Radius Selection</div>
              <Space wrap>
                {[0.5, 1, 2.5, 5, 10, 20].map(v => (
                  <Button key={v} type={radiusMiles===v ? 'primary' : 'default'} onClick={()=> setRadiusMiles(v)}>{v} {v===1 ? 'mile' : 'miles'}</Button>
                ))}
              </Space>
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Custom Radius: {radiusMiles} {radiusMiles===1?'mile':'miles'}</div>
              <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
                <div style={{ color:'#64748b', width: 60, textAlign:'left' }}>0.1 miles</div>
                <div style={{ flex: 1 }}>
                  <Slider min={0.1} max={20} step={0.1} value={radiusMiles} onChange={setRadiusMiles} />
                </div>
                <div style={{ color:'#64748b', width: 70, textAlign:'right' }}>20 miles</div>
              </div>
            </div>

            <div style={{ background:'#eef6ff', border:'1px solid #bfdbfe', borderRadius: 8, padding: 12, marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Current Settings</div>
              <div style={{ color:'#1e3a8a' }}>Geofence radius set to <b>{radiusMiles} {radiusMiles===1?'mile':'miles'}</b> around selected locations</div>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between', marginTop: 8 }}>
              <Button onClick={()=> setStep(3)}>Previous: Upload GPS</Button>
              <Button type="primary" onClick={() => { form.setFieldsValue({ geofence_radius_miles: radiusMiles }); setStep(5); }}>
                Next: View Results
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Results (preview, selection, export, finalize) */}
        {step === 5 && (
          <div>
            {adType === 'Geofence' ? (
              <div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 22, marginBottom: 4 }}>Geofence Campaign</div>
                  <div style={{ color: '#64748b' }}>Create Facebook ads targeting the selected geographic locations</div>
                </div>

                <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', color: '#065f46', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600 }}>
                    {selectedGpsIds.length} {selectedGpsIds.length === 1 ? 'location' : 'locations'} selected with {radiusMiles} {radiusMiles === 1 ? 'mile' : 'miles'} radius
                  </div>
                </div>

                <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Campaign Summary</div>
                  <div style={{ color: '#1e40af' }}>
                    <div>• Target Locations: {selectedGpsIds.length}</div>
                    <div>• Radius per Location: {radiusMiles} {radiusMiles === 1 ? 'mile' : 'miles'}</div>
                    <div>• Campaign Type: Geofence Targeting</div>
                    <div>• Selected Clients: {selectedUserId ? 1 : 0}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Selected Locations Preview:</div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {(gpsLocations.filter(l => selectedGpsIds.includes(l.id))).map((l) => (
                        <div key={l.id} style={{ padding: 10, borderBottom: '1px solid #f1f5f9' }}>
                          {l.address}
                        </div>
                      ))}
                      {selectedGpsIds.length === 0 && (
                        <div style={{ padding: 10, color:'#64748b' }}>No locations selected.</div>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Button onClick={() => setStep(4)}>Previous: Set Locations</Button>
                  <Button type="primary" disabled={selectedGpsIds.length === 0} onClick={() => { setFbWizardOpen(true); setFbStep(1); }}>
                    Launch Geofence Facebook Campaign
                  </Button>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop: 8 }}>
                  <Button disabled={selectedGpsIds.length === 0} onClick={() => { setGaWizardOpen(true); setGaStep(1); }}>
                    Launch Geofence Google Ads
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>Campaign Results</div>
                  {(profiles && profiles.length > 0) ? (
                    <Button type="primary" onClick={prepareResultsProfiles} loading={resultsLoading}>
                      {resultsProfiles.length > 0 ? 'Refresh Results' : 'Process Results'}
                    </Button>
                  ) : (
                    <Button type="primary" onClick={prepareResultsProfiles} loading={resultsLoading}>Load Profiles</Button>
                  )}
                </div>
                <div style={{ color: '#64748b', marginBottom: 12 }}>
                  Selected Profiles: {resultsSelectedIds.length || 0} | 
                  Available Profiles: {profiles?.length || 0} | 
                  Results Profiles: {resultsProfiles?.length || 0}
                  {resultsSelectedIds.length === 0 && (resultsProfiles?.length > 0 || profiles?.length > 0) && (
                    <span style={{ color: '#f59e0b', marginLeft: 8 }}>← Select profiles to launch campaign</span>
                  )}
                </div>

                <div style={{ background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Filter & Sort Profiles</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap: 10 }}>
                    <Select value="all" options={[{value:'all',label:'All distances'}]} />
                    <Select value="distance" options={[{value:'distance',label:'Distance'}]} />
                    <Select value="asc" options={[{value:'asc',label:'Closest first'}]} />
                  </div>
                </div>

                {(resultsProfiles && resultsProfiles.length > 0) || (profiles && profiles.length > 0) ? (
                  <div>
                    {(() => {
                      const currentProfiles = resultsProfiles.length > 0 ? resultsProfiles : profiles;
                      const startIndex = (resultsCurrentPage - 1) * resultsPageSize;
                      const endIndex = startIndex + resultsPageSize;
                      const paginatedProfiles = currentProfiles.slice(startIndex, endIndex);
                      
                      return (
                        <>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
                            <div style={{ color: '#64748b' }}>
                              Showing {startIndex + 1}-{Math.min(endIndex, currentProfiles.length)} of {currentProfiles.length} profiles
                            </div>
                            <Space>
                              <Button size="small" onClick={() => {
                                const pageProfiles = paginatedProfiles.map(p => p.id);
                                const allSelected = pageProfiles.every(id => resultsSelectedIds.includes(id));
                                if (allSelected) {
                                  setResultsSelectedIds(prev => prev.filter(id => !pageProfiles.includes(id)));
                                } else {
                                  setResultsSelectedIds(prev => [...new Set([...prev, ...pageProfiles])]);
                                }
                              }}>
                                {paginatedProfiles.every(p => resultsSelectedIds.includes(p.id)) ? 'Deselect Page' : 'Select Page'}
                              </Button>
                              <Button size="small" onClick={() => {
                                setResultsSelectedIds(currentProfiles.map(p=>p.id));
                              }}>
                                Select All {currentProfiles.length} Profiles
                              </Button>
                              <Button size="small" onClick={() => {
                                setResultsSelectedIds([]);
                              }}>
                                Clear All
                              </Button>
                            </Space>
                          </div>
                          
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
                            {paginatedProfiles.map(p => {
                              const selected = resultsSelectedIds.includes(p.id);
                              return (
                                <div key={p.id} onClick={() => setResultsSelectedIds(prev => selected ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                                  style={{ border: selected ? '2px solid #1677ff' : '1px solid #e2e8f0', borderRadius: 10, padding: 12, background:'#fff', cursor:'pointer' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                    <div style={{ width:32, height:32, borderRadius:16, background:'#e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{String(p.address||'P').trim()[0]?.toUpperCase()}</div>
                                    <div style={{ minWidth:0 }}>
                                      <div style={{ fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.standardizedAddress || p.address}</div>
                                      <div style={{ color:'#22c55e', fontSize:12 }}>
                                        {p.coordinates ? `Coords: ${p.coordinates}` : 'Distance data will appear here'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <div style={{ display:'flex', justifyContent:'center', marginBottom: 16 }}>
                            <Pagination
                              current={resultsCurrentPage}
                              pageSize={resultsPageSize}
                              total={currentProfiles.length}
                              onChange={(page) => setResultsCurrentPage(page)}
                              showSizeChanger={false}
                              showQuickJumper={true}
                              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} profiles`}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ color:'#64748b', marginBottom: 12 }}>No profiles loaded yet. Click "Proceed to Results" to load profiles from your audience file.</div>
                )}

                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius: 8, padding: 12, marginTop: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Launch & Export</div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <Button onClick={()=> setStep(4)}>Previous: Set Locations</Button>
                    <div style={{ display:'flex', gap:8 }}>
                      <Button onClick={() => {
                        const currentProfiles = resultsProfiles.length > 0 ? resultsProfiles : profiles;
                        exportProfilesCsv(currentProfiles.filter(p => resultsSelectedIds.includes(p.id)));
                      }} disabled={resultsSelectedIds.length===0}>Export CSV ({resultsSelectedIds.length})</Button>
                      <Button type="primary" disabled={resultsSelectedIds.length===0} onClick={() => { setFbWizardOpen(true); setFbStep(1); }}>Launch Facebook Campaign ({resultsSelectedIds.length} profiles)</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Classic form fallback when step >= 6 */}
        {step >= 6 && (
          <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 16 }}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input placeholder="Campaign name" />
            </Form.Item>
            <Form.Item name="client_id" label="Client ID">
              <InputNumber style={{ width: '100%' }} placeholder="Client ID (optional)" />
            </Form.Item>
            <Form.Item name="zone_id" label="Zone ID">
              <InputNumber style={{ width: '100%' }} placeholder="Zone ID (optional)" />
            </Form.Item>
            <Form.Item name="agency_id" label="Agency ID">
              <InputNumber style={{ width: '100%' }} placeholder="Agency ID (optional)" />
            </Form.Item>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Select a status' }]}> 
              <Select
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
                placeholder="Select status"
              />
            </Form.Item>
            <Form.Item name="start_date" label="Start Date">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="end_date" label="End Date">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
            <Form.Item name="budget" label="Budget">
              <InputNumber style={{ width: '100%' }} prefix="$" min={0} step={100} placeholder="Budget" />
            </Form.Item>
            <div style={{ display:'flex', justifyContent:'space-between' }}>
              <Button onClick={()=> setStep(1)}>Previous: Ad Type</Button>
              <Button type="primary" onClick={onSubmit} loading={loading}>{editingId ? 'Save Changes' : 'Create Campaign'}</Button>
            </div>
          </Form>
        )}

        {/* Facebook Ad Wizard Modal (global within Create Campaign modal) */}
        <Modal
          title={null}
          open={fbWizardOpen}
          onCancel={() => setFbWizardOpen(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          <div style={{ padding: 4 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: 4, 
                    background: '#1877F2', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: 'bold',
                    fontSize: 14
                  }}>f</div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>Create Facebook Ad</div>
                </div>
                <div style={{ color:'#64748b', fontSize: 12 }}>Step-by-step ad creation flow</div>
                {(() => {
                  const isGeoWizard = adType === 'Geofence';
                  const fbStepsList = isGeoWizard
                    ? ['Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad']
                    : ['Upload Audience','Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad'];
                  const fbTotalSteps = fbStepsList.length;
                  return <div style={{ color:'#94a3b8', fontSize: 12 }}>Step {fbStep} of {fbTotalSteps}</div>;
                })()}
              </div>
              {(() => {
                const isGeoWizard = adType === 'Geofence';
                const fbStepsList = isGeoWizard
                  ? ['Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad']
                  : ['Upload Audience','Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad'];
                const fbTotalSteps = fbStepsList.length;
                return <div style={{ color:'#64748b', fontSize: 12 }}>{Math.round((fbStep/fbTotalSteps)*100)}% complete</div>;
              })()}
            </div>

            <div style={{ display:'flex', justifyContent:'space-around', alignItems:'flex-start', marginBottom: 20, padding: '0 40px' }}>
              {(() => {
                const isGeoWizard = adType === 'Geofence';
                const fbStepsList = isGeoWizard
                  ? ['Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad']
                  : ['Upload Audience','Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad'];
                
                return fbStepsList;
              })().map((label, idx) => {
                const isGeoWizard = adType === 'Geofence';
                const stepIcons = isGeoWizard
                  ? ['⚙', '◯', '👁', '$', '✓', '📄']
                  : ['↗', '⚙', '👥', '👁', '$', '✓', '📄'];
                
                const stepIdx = idx+1;
                const done = fbStep > stepIdx;
                const active = fbStep === stepIdx;
                const icon = stepIcons[idx];
                
                return (
                  <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', flex: 1, minHeight: '70px' }}>
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: 16, 
                      background: done ? '#42b883' : active ? '#1877F2' : '#e4e6ea', 
                      color: done || active ? 'white' : '#65676b',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: 600,
                      fontSize: 14,
                      margin: '0 auto 6px auto',
                      border: 'none',
                      position: 'relative',
                      flexShrink: 0
                    }}>
                      <div style={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        lineHeight: 1,
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        {done ? '✓' : icon}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      fontWeight: 500,
                      color: done || active ? '#1c1e21' : '#65676b',
                      textAlign: 'center',
                      maxWidth: 80
                    }}>
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>

            {(() => {
              const isGeoWizard = adType === 'Geofence';
              const fbStepsList = isGeoWizard
                ? ['Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad']
                : ['Upload Audience','Campaign Setup','Audience','Ad Creative','Budget & Schedule','Review','Post Ad'];
              const fbTotalSteps = fbStepsList.length;
              return (
                <div style={{ height: 4, background:'#e4e6ea', borderRadius: 2, marginBottom: 24, margin: '0 20px 24px 20px' }}>
                  <div style={{ 
                    width: `${(fbStep/fbTotalSteps)*100}%`, 
                    height:'100%', 
                    background:'#1877F2',
                    borderRadius: 2,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              );
            })()}

            {((adType === 'Geofence' && fbStep === 3) || (adType !== 'Geofence' && fbStep === 4)) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Primary Text</div>
                  <Input.TextArea
                    rows={3}
                    value={fbDescription}
                    onChange={(e)=> setFbDescription(e.target.value)}
                    placeholder="Transform your home with our expert services..."
                    style={{ borderRadius: 8, border: '1px solid #dddfe2' }}
                  />
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop: 8 }}>
                    <div style={{ color:'#65676b', fontSize: 12 }}>{`${(fbDescription||'').length}/125 characters`}</div>
                    <Button size="small" icon="🤖" style={{ border: 'none', background: '#f0f2f5' }}>AI Generate</Button>
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Headline</div>
                  <Input 
                    maxLength={125} 
                    value={fbHeadline} 
                    onChange={(e)=> setFbHeadline(e.target.value)} 
                    placeholder="Get 20% Off This Summer!" 
                    style={{ borderRadius: 8, border: '1px solid #dddfe2' }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Description</div>
                  <Input 
                    value={fbDescription} 
                    onChange={(e)=> setFbDescription(e.target.value)} 
                    placeholder="Limited time offer for new customers" 
                    style={{ borderRadius: 8, border: '1px solid #dddfe2' }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Call to Action</div>
                  <Select
                    placeholder="Select call to action"
                    value={fbCta}
                    onChange={setFbCta}
                    options={[
                      { value:'LEARN_MORE', label:'Learn More' },
                      { value:'SIGN_UP', label:'Sign Up' },
                      { value:'BOOK_NOW', label:'Book Now' },
                      { value:'CONTACT_US', label:'Contact Us' },
                      { value:'GET_QUOTE', label:'Get Quote' },
                      { value:'SHOP_NOW', label:'Shop Now' },
                    ]}
                    style={{ width:'100%', borderRadius: 8 }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Website URL</div>
                  <Input 
                    value={fbWebsiteUrl} 
                    onChange={(e)=> setFbWebsiteUrl(e.target.value)} 
                    placeholder="https://yourbusiness.com" 
                    style={{ borderRadius: 8, border: '1px solid #dddfe2' }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#1c1e21' }}>Ad Media</div>
                  
                  {/* Media Type Selection */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <Button 
                        type={fbMediaType === 'image' ? 'primary' : 'default'}
                        onClick={() => {
                          setFbMediaType('image');
                          setFbVideoFile(null);
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        📷 Image
                      </Button>
                      <Button 
                        type={fbMediaType === 'video' ? 'primary' : 'default'}
                        onClick={() => {
                          setFbMediaType('video');
                          setFbImageFile(null);
                        }}
                        style={{ borderRadius: 6 }}
                      >
                        🎥 Video
                      </Button>
                    </div>
                  </div>

                  {/* Upload Area */}
                  <Upload.Dragger
                    name="file"
                    multiple={false}
                    accept={fbMediaType === 'image' ? 'image/*' : 'video/*'}
                    showUploadList={false}
                    customRequest={async ({ file, onSuccess, onError }) => {
                      try {
                        if (fbMediaType === 'image') {
                          setFbImageFile(file);
                          setFbVideoFile(null);
                        } else {
                          setFbVideoFile(file);
                          setFbImageFile(null);
                        }
                        onSuccess && onSuccess({}, file);
                      } catch (e) {
                        onError && onError(e);
                      }
                    }}
                    style={{ 
                      borderRadius: 8, 
                      border: '2px dashed #dddfe2',
                      background: '#f8f9fa'
                    }}
                  >
                    <div style={{ textAlign:'center', color:'#65676b', padding: 20 }}>
                      {(fbMediaType === 'image' && fbImageFile) || (fbMediaType === 'video' && fbVideoFile) ? (
                        <div>
                          <div style={{ fontSize: 16, marginBottom: 4 }}>
                            {fbMediaType === 'image' ? 'Image' : 'Video'} selected. Click to replace.
                          </div>
                          <div style={{ fontSize: 12, color: '#8a8d91' }}>
                            {fbMediaType === 'image' && fbImageFile ? fbImageFile.name : ''}
                            {fbMediaType === 'video' && fbVideoFile ? fbVideoFile.name : ''}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 48, marginBottom: 8 }}>
                            {fbMediaType === 'image' ? '📤' : '🎬'}
                          </div>
                          <div style={{ fontSize: 16, marginBottom: 4 }}>
                            Upload {fbMediaType} or drag and drop
                          </div>
                          <div style={{ fontSize: 12, color: '#8a8d91' }}>
                            {fbMediaType === 'image' 
                              ? 'Recommended: 1200×628px (JPG, PNG, GIF)'
                              : 'Recommended: MP4, MOV, AVI (Max 4GB, 240 minutes)'
                            }
                          </div>
                        </>
                      )}
                    </div>
                  </Upload.Dragger>

                  {/* Preview */}
                  {fbImageFile && fbMediaType === 'image' && (
                    <div style={{ marginTop: 12 }}>
                      <img
                        alt="ad-preview"
                        src={URL.createObjectURL(fbImageFile)}
                        style={{ maxWidth: '100%', borderRadius: 8, border:'1px solid #dddfe2' }}
                      />
                    </div>
                  )}
                  
                  {fbVideoFile && fbMediaType === 'video' && (
                    <div style={{ marginTop: 12 }}>
                      <video
                        controls
                        style={{ maxWidth: '100%', borderRadius: 8, border:'1px solid #dddfe2' }}
                      >
                        <source src={URL.createObjectURL(fbVideoFile)} type={fbVideoFile.type} />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #dddfe2' }}>
                  <Button 
                    onClick={()=> setFbStep(Math.max(1, fbStep-1))}
                    style={{ borderRadius: 6, border: '1px solid #dddfe2' }}
                  >
                    ← Back
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={()=> {
                      const urlOk = /^(https?:\/\/)[\w.-]+(\.[\w.-]+)+[/#?]?.*$/.test(String(fbWebsiteUrl||'').trim());
                      if (!fbHeadline || String(fbHeadline).trim().length === 0) {
                        message.error('Headline is required');
                        return;
                      }
                      if (!urlOk) {
                        message.error('Please enter a valid Website URL starting with http:// or https://');
                        return;
                      }
                      setFbStep(fbStep+1);
                    }}
                    style={{ 
                      borderRadius: 6, 
                      background: '#1877f2', 
                      borderColor: '#1877f2',
                      fontWeight: 600
                    }}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {((adType === 'Geofence' && fbStep === 1) || (adType !== 'Geofence' && fbStep === 2)) && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Campaign Setup</div>
                <div style={{ display:'grid', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Name</div>
                    <Input value={fbCampaignName} onChange={(e)=> setFbCampaignName(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Objective</div>
                    <Select
                      placeholder="Select campaign objective"
                      value={fbObjective}
                      onChange={setFbObjective}
                      options={[
                        { value:'REACH', label:'Reach' },
                        { value:'AWARENESS', label:'Awareness' },
                        { value:'TRAFFIC', label:'Traffic' },
                        { value:'LEADS', label:'Leads' },
                        { value:'SALES', label:'Sales' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Ad Name</div>
                    <Input value={fbAdName} onChange={(e)=> setFbAdName(e.target.value)} />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
                  <Button onClick={()=> { if (fbStep === 1 && adType === 'Geofence') { setFbWizardOpen(false); } else { setFbStep(Math.max(1, fbStep-1)); } }}>Back</Button>
                  <Button type="primary" onClick={()=> setFbStep(fbStep+1)}>Next</Button>
                </div>
              </div>
            )}

            {((adType === 'Geofence' && fbStep === 2) || (adType !== 'Geofence' && fbStep === 3)) && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Audience</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Age Range</div>
                    <div style={{ display:'flex', gap: 8 }}>
                      <InputNumber min={13} max={fbAgeMax} value={fbAgeMin} onChange={(v)=> setFbAgeMin(v)} style={{ width: '100%' }} />
                      <div style={{ alignSelf:'center', color:'#64748b' }}>to</div>
                      <InputNumber min={fbAgeMin} max={65} value={fbAgeMax} onChange={(v)=> setFbAgeMax(v)} style={{ width: '100%' }} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Gender</div>
                    <Select value={fbGender} onChange={setFbGender} options={[{value:'all',label:'All'},{value:'male',label:'Male'},{value:'female',label:'Female'}]} />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Location</div>
                  {(() => {
                    const addrList = (geotabAddresses && geotabAddresses.length > 0)
                      ? geotabAddresses.map(a => a.address).filter(Boolean)
                      : (gpsLocations || []).map(a => a.address).filter(Boolean);
                    const unique = Array.from(new Set(addrList));
                    const ALL_VALUE = '__ALL_LOCATIONS_FB__';
                    const options = [
                      { value: ALL_VALUE, label: 'Select All' },
                      ...unique.map(v => ({ value: v, label: v }))
                    ];
                    const handleChange = (vals) => {
                      if (Array.isArray(vals) && vals.includes(ALL_VALUE)) {
                        setFbLocation(unique);
                      } else {
                        setFbLocation(vals || []);
                      }
                    };
                    return (
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="Select one or more locations"
                        value={fbLocation}
                        onChange={handleChange}
                        options={options}
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                      />
                    );
                  })()}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Interests (suggested)</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap: 8 }}>
                    {['Home improvement','Small business','Local services','Home renovation'].map((label)=> {
                      const selected = fbInterests.includes(label);
                      return (
                        <Tag.CheckableTag
                          key={label}
                          checked={selected}
                          onChange={(checked)=> {
                            if (checked) setFbInterests([...(fbInterests||[]), label]);
                            else setFbInterests((fbInterests||[]).filter(i=> i!==label));
                          }}
                        >
                          {label}
                        </Tag.CheckableTag>
                      );
                    })}
                  </div>
                </div>
                <div style={{ marginTop: 8, display:'flex', justifyContent:'space-between' }}>
                  <Button onClick={()=> setFbStep(Math.max(1, fbStep-1))}>Back</Button>
                  <Button type="primary" onClick={()=> setFbStep(fbStep+1)}>Next</Button>
                </div>
              </div>
            )}

            {(adType !== 'Geofence' && fbStep === 1) && (
              <div>
                <div style={{ textAlign:'center', margin: '12px 0' }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>Upload Custom Audience</div>
                  <div style={{ color:'#64748b' }}>{resultsSelectedIds.length} profiles will be uploaded to Facebook as a custom audience for targeting.</div>
                </div>
                {resultsSelectedIds.length > 0 ? (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>📤</div>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Upload Custom Audience</div>
                    <div style={{ color: '#64748b', marginBottom: 12 }}>
                      {resultsSelectedIds.length} profiles will be uploaded to Facebook as a custom audience for targeting.
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <Upload.Dragger
                      name="file"
                      multiple={false}
                      accept=".csv,text/csv"
                      showUploadList={false}
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          setFbAudienceCsvFile(file);
                          // Count rows in CSV for quick feedback
                          const text = await file.text();
                          const lines = String(text||'').split(/\r?\n/).filter(l => l.trim().length > 0);
                          setFbAudienceRowCount(Math.max(0, lines.length - 1));
                          message.success('CSV selected');
                          onSuccess && onSuccess({}, file);
                        } catch (e) {
                          message.error('Failed to read CSV');
                          onError && onError(e);
                        }
                      }}
                      style={{ borderRadius: 8 }}
                    >
                      <div style={{ textAlign:'center', color:'#64748b' }}>
                        {fbAudienceCsvFile ? (
                          <>
                            <div style={{ fontWeight: 600, color:'#0f172a', marginBottom: 4 }}>{fbAudienceCsvFile.name}</div>
                            <div>{fbAudienceRowCount != null ? `${fbAudienceRowCount} rows detected` : 'CSV loaded'}</div>
                            <div style={{ marginTop: 6 }}>Click to replace</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 24, marginBottom: 6 }}>⬆</div>
                            <div>Click or drag CSV to upload your custom audience</div>
                            <div style={{ fontSize: 12 }}>Accepted: .csv</div>
                          </>
                        )}
                      </div>
                    </Upload.Dragger>
                  </div>
                )}
                <div style={{ border:'1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10 }}>Audience Preview</div>
                  {(() => {
                    const currentProfiles = resultsProfiles.length > 0 ? resultsProfiles : profiles || [];
                    const selectedProfiles = currentProfiles.filter(p => resultsSelectedIds.includes(p.id));
                    
                    // Debug info
                    console.log('Audience Preview Debug:');
                    console.log('- currentProfiles length:', currentProfiles.length);
                    console.log('- resultsSelectedIds:', resultsSelectedIds);
                    console.log('- selectedProfiles length:', selectedProfiles.length);
                    if (selectedProfiles.length > 0) {
                      console.log('- Sample profile:', selectedProfiles[0]);
                    }
                    
                    return (
                      <div style={{ display:'grid', gridTemplateColumns:'1fr auto', rowGap: 8 }}>
                        <div>Total Profiles:</div><div style={{ fontWeight: 600, color: resultsSelectedIds.length > 0 ? '#1677ff' : '#666' }}>
                          {fbAudienceRowCount != null ? fbAudienceRowCount : resultsSelectedIds.length}
                        </div>
                        <div>Email Addresses:</div><div style={{ fontWeight: 600, color: '#666' }}>{(() => {
                          if (fbAudienceRowCount != null) return 0;
                          return selectedProfiles.filter(p => p.email && String(p.email).trim().length > 0).length;
                        })()}</div>
                        <div>Phone Numbers:</div><div style={{ fontWeight: 600, color: '#666' }}>{(() => {
                          if (fbAudienceRowCount != null) return 0;
                          return selectedProfiles.filter(p => p.phone && String(p.phone).trim().length > 0).length;
                        })()}</div>
                        <div>Addresses:</div><div style={{ fontWeight: 600, color: selectedProfiles.length > 0 ? '#1677ff' : '#666' }}>{(() => {
                          if (fbAudienceRowCount != null) return fbAudienceRowCount;
                          return selectedProfiles.filter(p => (p.address || p.standardizedAddress) && String(p.address || p.standardizedAddress).trim().length > 0).length;
                        })()}</div>
                        <div>Coordinates:</div><div style={{ fontWeight: 600, color: selectedProfiles.filter(p => p.coordinates).length > 0 ? '#1677ff' : '#666' }}>{(() => {
                          if (fbAudienceRowCount != null) return fbAudienceRowCount;
                          return selectedProfiles.filter(p => p.coordinates && String(p.coordinates).trim().length > 0).length;
                        })()}</div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
                  <Space>
                    <Button type="primary" disabled={!fbAudienceCsvFile} loading={fbAudienceUploading} onClick={async ()=>{
                      if (!fbAudienceCsvFile) return;
                      try {
                        setFbAudienceUploading(true);
                        
                        const fd = new FormData();
                        fd.append('file', fbAudienceCsvFile);
                        const { data } = await axios.post('/api/media', fd, { headers: { ...authHeader() } });
                        const newId = data?.id;
                        setFbAudienceFileId(newId || null);
                        message.success('Audience uploaded to Facebook');
                      } catch (e) {
                        message.error(e?.response?.data?.error || 'Upload failed');
                      } finally {
                        setFbAudienceUploading(false);
                      }
                    }}>
                      Upload CSV
                    </Button>
                    {fbAudienceFileId && (
                      <Tag color="blue">File ID: {fbAudienceFileId}</Tag>
                    )}
                  </Space>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Custom Audience Name</div>
                  <Input value={fbAudienceName} onChange={(e)=> setFbAudienceName(e.target.value)} />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Description (Optional)</div>
                  <Input.TextArea rows={3} value={fbAudienceDesc} onChange={(e)=> setFbAudienceDesc(e.target.value)} />
                </div>
                <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', color:'#1e3a8a', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  Privacy Notice: This audience will be uploaded to Facebook securely and hashed for privacy. Facebook will match this data with their users while maintaining privacy standards.
                </div>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <Button onClick={()=> setFbWizardOpen(false)}>Back</Button>
                  <Button 
                    type="primary" 
                    onClick={()=> setFbStep(fbStep+1)}
                    style={{ background: '#1677ff', borderColor: '#1677ff' }}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {((adType === 'Geofence' && fbStep === 4) || (adType !== 'Geofence' && fbStep === 5)) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#1c1e21' }}>Budget Type</div>
                  <Select
                    value={fbBudgetType}
                    onChange={setFbBudgetType}
                    options={[
                      { value: 'daily', label: 'Daily Budget' },
                      { value: 'lifetime', label: 'Lifetime Budget (coming soon)' },
                    ]}
                    style={{ width: '100%', borderRadius: 8 }}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#1c1e21' }}>Daily Budget ($)</div>
                  <InputNumber 
                    min={1} 
                    step={1} 
                    value={fbDailyBudget} 
                    onChange={v => setFbDailyBudget(v || 0)} 
                    style={{ width: '100%', borderRadius: 8 }}
                    size="large"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#1c1e21' }}>Campaign Duration (days)</div>
                  <InputNumber 
                    min={1} 
                    step={1} 
                    value={fbDurationDays} 
                    onChange={v => setFbDurationDays(v || 1)} 
                    style={{ width: '100%', borderRadius: 8 }}
                    size="large"
                  />
                </div>

                <div style={{ 
                  background:'#f0f2f5', 
                  border:'1px solid #dddfe2', 
                  borderRadius: 8, 
                  padding: 16, 
                  marginBottom: 20 
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#1c1e21' }}>Budget Summary</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr auto', rowGap: 8, color: '#65676b' }}>
                    <div>Daily Budget:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>${Number(fbDailyBudget||0).toLocaleString()}</div>
                    <div>Campaign Duration:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{Number(fbDurationDays||0)} days</div>
                    <div style={{ fontWeight: 600, color: '#1c1e21', borderTop: '1px solid #dddfe2', paddingTop: 8 }}>Total Estimated Spend:</div>
                    <div style={{ fontWeight: 600, color: '#1c1e21', borderTop: '1px solid #dddfe2', paddingTop: 8 }}>${Number((fbDailyBudget||0) * (fbDurationDays||0)).toLocaleString()}</div>
                  </div>
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #dddfe2' }}>
                  <Button 
                    onClick={()=> setFbStep(Math.max(1, fbStep-1))}
                    style={{ borderRadius: 6, border: '1px solid #dddfe2' }}
                  >
                    ← Back
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={()=> setFbStep(fbStep+1)}
                    style={{ 
                      borderRadius: 6, 
                      background: '#1877f2', 
                      borderColor: '#1877f2',
                      fontWeight: 600
                    }}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {((adType === 'Geofence' && fbStep === 5) || (adType !== 'Geofence' && fbStep === 6)) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16, color: '#1c1e21' }}>Campaign Details</div>
                  <div style={{ background: '#f8f9fa', border: '1px solid #dddfe2', borderRadius: 8, padding: 16 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', rowGap: 12, color: '#65676b' }}>
                      <div>Campaign Name:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{fbCampaignName || '-'}</div>
                      <div>Objective:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{fbObjective || '-'}</div>
                      <div>Ad Name:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{fbAdName || '-'}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16, color: '#1c1e21' }}>Audience</div>
                  <div style={{ background: '#f8f9fa', border: '1px solid #dddfe2', borderRadius: 8, padding: 16 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', rowGap: 12, color: '#65676b' }}>
                      <div>Age:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{`${Number(fbAgeMin||0)} - ${Number(fbAgeMax||0)}`}</div>
                      <div>Gender:</div><div style={{ fontWeight: 600, color: '#1c1e21', textTransform:'capitalize' }}>{fbGender}</div>
                      <div>Location:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{(Array.isArray(fbLocation) && fbLocation.length > 0) ? fbLocation.join(', ') : '-'}</div>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16, color: '#1c1e21' }}>Budget</div>
                  <div style={{ background: '#f8f9fa', border: '1px solid #dddfe2', borderRadius: 8, padding: 16 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', rowGap: 12, color: '#65676b' }}>
                      <div>Daily Budget:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>${Number(fbDailyBudget||0).toLocaleString()}</div>
                      <div>Duration:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{Number(fbDurationDays||0)} days</div>
                      <div style={{ fontWeight: 600, color: '#1c1e21', borderTop: '1px solid #dddfe2', paddingTop: 12 }}>Total Budget:</div>
                      <div style={{ fontWeight: 600, color: '#1c1e21', borderTop: '1px solid #dddfe2', paddingTop: 12 }}>${Number((fbDailyBudget||0) * (fbDurationDays||0)).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid #dddfe2' }}>
                  <Button 
                    onClick={()=> setFbStep(Math.max(1, fbStep-1))}
                    style={{ borderRadius: 6, border: '1px solid #dddfe2' }}
                  >
                    ← Back
                  </Button>
                  <Button 
                    type="primary" 
                    onClick={()=> setFbStep(fbStep+1)}
                    style={{ 
                      borderRadius: 6, 
                      background: '#1877f2', 
                      borderColor: '#1877f2',
                      fontWeight: 600
                    }}
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {((adType === 'Geofence' && fbStep === 6) || (adType !== 'Geofence' && fbStep === 7)) && (
              <div style={{ textAlign:'center', marginTop: 12 }}>
                <div style={{ width:56, height:56, borderRadius:28, background:'#dcfce7', color:'#16a34a', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', fontSize:28 }}>
                  <CheckCircleFilled />
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Ready to Post!</div>
                <div style={{ color:'#64748b', marginBottom: 24 }}>Your Facebook ad is ready to go live</div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 8 }}>
                  <Button onClick={()=> setFbStep(6)}>Back</Button>
                  <Button type="primary" icon={<FacebookFilled />} style={{ background:'#1877F2', borderColor:'#1877F2' }} onClick={()=> { try { window.open(FB_ADS_URL, '_blank', 'noopener,noreferrer'); } catch(_) {} setFbWizardOpen(false); }}>
                    Post to Facebook
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal>

        <Modal
          title={null}
          open={gaWizardOpen}
          onCancel={() => setGaWizardOpen(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          <div style={{ padding: 4 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ 
                    width: 24, 
                    height: 24, 
                    borderRadius: 4, 
                    background: '#1a73e8', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: 'bold',
                    fontSize: 14
                  }}>G</div>
                  <div style={{ fontWeight: 700, fontSize: 18 }}>Create Google Ads Campaign</div>
                </div>
                {(() => {
                  const isGeoWizard = adType === 'Geofence';
                  const gaStepsList = isGeoWizard
                    ? ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad']
                    : ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad'];
                  const gaTotalSteps = gaStepsList.length;
                  return <div style={{ color:'#94a3b8', fontSize: 12 }}>Step {gaStep} of {gaTotalSteps}</div>;
                })()}
              </div>
              {(() => {
                const isGeoWizard = adType === 'Geofence';
                const gaStepsList = isGeoWizard
                  ? ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad']
                  : ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad'];
                const gaTotalSteps = gaStepsList.length;
                return <div style={{ color:'#64748b', fontSize: 12 }}>{Math.round((gaStep/gaTotalSteps)*100)}% complete</div>;
              })()}
            </div>

            <div style={{ display:'flex', justifyContent:'space-around', alignItems:'flex-start', marginBottom: 20, padding: '0 40px' }}>
              {(() => {
                const isGeoWizard = adType === 'Geofence';
                const gaStepsList = isGeoWizard
                  ? ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad']
                  : ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad'];
                return gaStepsList;
              })().map((label, idx) => {
                const stepIcons = ['⚙', '🎯', '📝', '$', '✓', '📄'];
                const stepIdx = idx+1;
                const done = gaStep > stepIdx;
                const active = gaStep === stepIdx;
                const icon = stepIcons[idx];
                return (
                  <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', flex: 1, minHeight: '70px' }}>
                    <div style={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: 16, 
                      background: done ? '#34a853' : active ? '#1a73e8' : '#e4e6ea', 
                      color: done || active ? 'white' : '#65676b',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: 600,
                      fontSize: 14,
                      margin: '0 auto 6px auto',
                      border: 'none',
                      position: 'relative',
                      flexShrink: 0
                    }}>
                      <div style={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        lineHeight: 1,
                        fontFamily: 'Arial, sans-serif',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}>
                        {done ? '✓' : icon}
                      </div>
                    </div>
                    <div style={{ 
                      fontSize: 12, 
                      fontWeight: 500,
                      color: done || active ? '#1c1e21' : '#65676b',
                      textAlign: 'center',
                      maxWidth: 80
                    }}>
                      {label}
                    </div>
                  </div>
                );
              })}
            </div>

            {(() => {
              const isGeoWizard = adType === 'Geofence';
              const gaStepsList = isGeoWizard
                ? ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad']
                : ['Campaign Setup','Targeting','Ad Creative','Budget & Schedule','Review','Post Ad'];
              const gaTotalSteps = gaStepsList.length;
              return (
                <div style={{ height: 4, background:'#e4e6ea', borderRadius: 2, marginBottom: 24, margin: '0 20px 24px 20px' }}>
                  <div style={{ 
                    width: `${(gaStep/gaTotalSteps)*100}%`, 
                    height:'100%', 
                    background:'#1a73e8',
                    borderRadius: 2,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              );
            })()}

            {(gaStep === 1) && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Campaign Setup</div>
                <div style={{ display:'grid', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Name</div>
                    <Input value={gaCampaignName} onChange={(e)=> setGaCampaignName(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Type</div>
                    <Select
                      placeholder="Select campaign type"
                      value={gaCampaignType}
                      onChange={setGaCampaignType}
                      options={[
                        { value:'SEARCH', label:'Search' },
                        { value:'DISPLAY', label:'Display' },
                        { value:'VIDEO', label:'Video' },
                        { value:'SHOPPING', label:'Shopping' },
                        { value:'PMAX', label:'Performance Max' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Campaign Goal</div>
                    <Select
                      placeholder="Select campaign goal"
                      value={gaCampaignGoal}
                      onChange={setGaCampaignGoal}
                      options={[
                        { value:'SALES', label:'Sales' },
                        { value:'LEADS', label:'Leads' },
                        { value:'WEBSITE_TRAFFIC', label:'Website traffic' },
                        { value:'BRAND_AWARENESS', label:'Brand awareness' },
                        { value:'LOCAL_STORE_VISITS', label:'Local store visits' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
                  <Button onClick={()=> setGaWizardOpen(false)}>Back</Button>
                  <Button type="primary" onClick={()=> setGaStep(2)}>Next</Button>
                </div>
              </div>
            )}

            {(gaStep === 2) && (
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Targeting</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Target Location(s)</div>
                    {(() => {
                      const addrList = (geotabAddresses && geotabAddresses.length > 0)
                        ? geotabAddresses.map(a => a.address).filter(Boolean)
                        : (gpsLocations || []).map(a => a.address).filter(Boolean);
                      const unique = Array.from(new Set(addrList));
                      const ALL_VALUE = '__ALL_LOCATIONS__';
                      const options = [
                        { value: ALL_VALUE, label: 'Select All' },
                        ...unique.map(v => ({ value: v, label: v }))
                      ];
                      const handleChange = (vals) => {
                        if (Array.isArray(vals) && vals.includes(ALL_VALUE)) {
                          // Select all real location values (exclude Select All token)
                          setGaLocations(unique);
                        } else {
                          setGaLocations(vals || []);
                        }
                      };
                      return (
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          placeholder="Select one or more locations"
                          value={gaLocations}
                          onChange={handleChange}
                          options={options}
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                        />
                      );
                    })()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Target Language(s)</div>
                    <Input placeholder="English, Spanish" value={gaLanguages} onChange={(e)=> setGaLanguages(e.target.value)} />
                  </div>
                </div>
                <div style={{ display:'grid', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Ad Group Name</div>
                    <Input value={gaAdGroupName} onChange={(e)=> setGaAdGroupName(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Keywords</div>
                    <Input.TextArea rows={3} placeholder="keyword 1, keyword 2" value={gaKeywords} onChange={(e)=> setGaKeywords(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Audience Targeting (optional)</div>
                    <Input placeholder="In-market: Home services; Custom: Competitors" value={gaAudience} onChange={(e)=> setGaAudience(e.target.value)} />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 8 }}>
                  <Button onClick={()=> setGaStep(1)}>Back</Button>
                  <Button type="primary" onClick={()=> setGaStep(3)}>Next</Button>
                </div>
              </div>
            )}

            {(gaStep === 3) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Ad Creative</div>
                <div style={{ display:'grid', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Final URL</div>
                    <Input value={gaFinalUrl} onChange={(e)=> setGaFinalUrl(e.target.value)} placeholder="https://yourbusiness.com" />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Display Path (optional)</div>
                      <Input value={gaDisplayPath} onChange={(e)=> setGaDisplayPath(e.target.value)} placeholder="services/home" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Call-to-Action</div>
                      <Select value={gaCta} onChange={setGaCta} placeholder="Select CTA" options={[{value:'LEARN_MORE',label:'Learn more'},{value:'SHOP_NOW',label:'Shop now'},{value:'CONTACT_US',label:'Contact us'},{value:'GET_QUOTE',label:'Get quote'}]} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Headlines</div>
                    <div style={{ display:'grid', gap: 8 }}>
                      <Input maxLength={30} placeholder="Headline 1" value={gaHeadline1} onChange={(e)=> setGaHeadline1(e.target.value)} />
                      <Input maxLength={30} placeholder="Headline 2" value={gaHeadline2} onChange={(e)=> setGaHeadline2(e.target.value)} />
                      <Input maxLength={30} placeholder="Headline 3 (optional)" value={gaHeadline3} onChange={(e)=> setGaHeadline3(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Descriptions</div>
                    <div style={{ display:'grid', gap: 8 }}>
                      <Input maxLength={90} placeholder="Description 1" value={gaDesc1} onChange={(e)=> setGaDesc1(e.target.value)} />
                      <Input maxLength={90} placeholder="Description 2 (optional)" value={gaDesc2} onChange={(e)=> setGaDesc2(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Ad Extensions (optional)</div>
                    <Input.TextArea rows={3} placeholder="Sitelinks, Callouts, Call" value={gaAdExtensions} onChange={(e)=> setGaAdExtensions(e.target.value)} />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Tracking Template / UTM (optional)</div>
                      <Input placeholder="{lpurl}?utm_source=google&utm_medium=cpc" value={gaTrackingTemplate} onChange={(e)=> setGaTrackingTemplate(e.target.value)} />
                    </div>
                    <div style={{ alignSelf: 'end' }}>
                      <Checkbox checked={gaConversionTracking} onChange={(e)=> setGaConversionTracking(e.target.checked)}>Conversion Tracking Setup</Checkbox>
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
                  <Button onClick={()=> setGaStep(2)}>Back</Button>
                  <Button type="primary" onClick={()=> setGaStep(4)}>
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {(gaStep === 4) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Budget & Schedule</div>
                <div style={{ display:'grid', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Budget ($)</div>
                    <InputNumber min={1} step={1} value={gaBudget} onChange={(v)=> setGaBudget(v || 0)} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Bidding Strategy</div>
                    <Select value={gaBiddingStrategy} onChange={setGaBiddingStrategy} placeholder="Select strategy" options={[{value:'MAX_CLICKS',label:'Maximize Clicks'},{value:'MAX_CONV',label:'Maximize Conversions'},{value:'TCPA',label:'Target CPA'},{value:'TROAS',label:'Target ROAS'}]} />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Start Date</div>
                      <DatePicker value={gaStartDate} onChange={(d)=> setGaStartDate(d)} style={{ width: '100%' }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>End Date (optional)</div>
                      <DatePicker value={gaEndDate} onChange={(d)=> setGaEndDate(d)} style={{ width: '100%' }} />
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
                  <Button onClick={()=> setGaStep(3)}>Back</Button>
                  <Button type="primary" onClick={()=> setGaStep(5)}>
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {(gaStep === 5) && (
              <div style={{ padding: '0 20px' }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>Review</div>
                <div style={{ background: '#f8f9fa', border: '1px solid #dddfe2', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', rowGap: 10, color: '#65676b' }}>
                    <div>Campaign Name:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaCampaignName || '-'}</div>
                    <div>Type:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaCampaignType || '-'}</div>
                    <div>Goal:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaCampaignGoal || '-'}</div>
                    <div>Ad Group:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaAdGroupName || '-'}</div>
                    <div>Locations:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{(Array.isArray(gaLocations) && gaLocations.length > 0) ? gaLocations.join(', ') : '-'}</div>
                    <div>Languages:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaLanguages || '-'}</div>
                    <div>Keywords:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaKeywords || '-'}</div>
                    <div>Final URL:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaFinalUrl || '-'}</div>
                    <div>Display Path:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaDisplayPath || '-'}</div>
                    <div>CTA:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaCta || '-'}</div>
                    <div>Budget:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>${Number(gaBudget||0).toLocaleString()}</div>
                    <div>Bidding:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaBiddingStrategy || '-'}</div>
                    <div>Start Date:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaStartDate ? gaStartDate.format('YYYY-MM-DD') : '-'}</div>
                    <div>End Date:</div><div style={{ fontWeight: 600, color: '#1c1e21' }}>{gaEndDate ? gaEndDate.format('YYYY-MM-DD') : '-'}</div>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 16 }}>
                  <Button onClick={()=> setGaStep(4)}>Back</Button>
                  <Button type="primary" onClick={()=> setGaStep(6)}>
                    Next →
                  </Button>
                </div>
              </div>
            )}

            {(gaStep === 6) && (
              <div style={{ textAlign:'center', marginTop: 12 }}>
                <div style={{ width:56, height:56, borderRadius:28, background:'#e6f4ea', color:'#34a853', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px', fontSize:28 }}>
                  <CheckCircleFilled />
                </div>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Ready to Post!</div>
                <div style={{ color:'#64748b', marginBottom: 24 }}>Your Google Ads campaign is ready to go live</div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop: 8 }}>
                  <Button onClick={()=> setGaStep(5)}>Back</Button>
                  <Button type="primary" style={{ background:'#1a73e8', borderColor:'#1a73e8' }} onClick={()=> { try { window.open(GA_ADS_URL, '_blank', 'noopener,noreferrer'); } catch(_) {} setGaWizardOpen(false); }}>
                    Post to Google Ads
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </Modal>
    </div>
  );
};

export default AllCampaignsList;
